import AdminReviewAuthor from '../components/AdminReviewAuthor';
import useBackHandler from '../hooks/useBackHandler';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import { sortAdminNewest as sortByDateDesc } from '../utils/adminChronology';
import { sortAdminReviewSubmissions } from '../utils/adminReviewOrder';
import AdminPurchaseCustomers from '../components/AdminPurchaseCustomers';
import ProductAnalyticsDashboard from '../components/ProductAnalyticsDashboard';
import AdminOperations from '../components/AdminOperations';
import PageLoading from '../components/PageLoading';
import LoadingLabel from '../components/LoadingLabel';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Card, CardContent, CircularProgress, TextField, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
    Tabs, Tab, useMediaQuery,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplayIcon from '@mui/icons-material/Replay';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PageHeader from '../components/PageHeader';
import AppToast from '../components/AppToast';
import {
    getAdminUsageSummary, getAdminUsageTrend, collectStats, getDeploymentInfo,
    getMedicationDistribution, getImageRegistrationDistribution, getTodayMedicationActions, getAdminAdStats,
    getNotificationPatterns, getAdminPageDurationStats,
    getDeviceInfo, getMedicineAliasCandidates, approveMedicineAliasCandidate, rejectMedicineAliasCandidate,
    getEntitlements, grantAdminAdFree, revokeAdminAdFree, getAdminStoreReviewCtaStats,
    getAdminReviews, replyAdminReview, hideAdminReview,
} from '../api';
import { resetStoreReviewLocalState } from '../utils/storeReview';

const ADMIN_KEY = 'admin_authenticated';
const ADMIN_PASSWORD_KEY = 'admin_password';
const UUID_KEY = 'pill_reminder_uuid';
const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const ENTITLEMENT_CACHE_KEY = 'pill_reminder_entitlements';
const EMPTY_DISTRIBUTION_BUCKETS = Array.from({ length: 11 }, (_, n) => ({ label: `${n}개`, count: 0 }));
const EMPTY_MEDICATION_DISTRIBUTION = {
    totalGroups: 0,
    avgMedsPerGroup: 0,
    maxMedsPerGroup: 0,
    buckets: EMPTY_DISTRIBUTION_BUCKETS,
};
const EMPTY_IMAGE_DISTRIBUTION = {
    totalGroups: 0,
    avgImagesPerGroup: 0,
    maxImagesPerGroup: 0,
    buckets: EMPTY_DISTRIBUTION_BUCKETS,
};
const EMPTY_NOTIFICATION_PATTERNS = {
    totalActiveMedications: 0,
    totalMedicationGroups: 0,
    medicationMix: [],
    groupAdoption: [],
    groupModes: [],
    pushReadiness: {
        readyGroups: 0,
        rate: 0,
        pushAlertReadyGroups: 0,
        pushAlertReadyRate: 0,
        ringtoneAlertReadyGroups: 0,
        ringtoneAlertReadyRate: 0,
    },
    reminderSettings: [],
};
const EMPTY_TODAY_MEDICATION_ACTIONS = {
    date: '',
    totalDevices: 0,
    totalGroups: 0,
    actionUsers: 0,
    takenGroups: 0,
    skippedGroups: 0,
    deferredGroups: 0,
    takenLogs: 0,
    skippedLogs: 0,
    deferredLogs: 0,
};
const EMPTY_PAGE_DURATION_STATS = {
    totalEvents: 0,
    uniqueDevices: 0,
    avgDurationMs: 0,
    avgDurationSec: 0,
    byPage: [],
};
const EMPTY_AD_STATS = {
    totalEvents: 0,
    requestEvents: 0,
    nativeEvents: 0,
    showEvents: 0,
    uniqueDevices: 0,
    uniqueGroups: 0,
    byFeature: [],
    byAdType: [],
    byEventType: [],
    nativeByEventType: [],
    failureReasons: [],
    trend: [],
    requestTrend: [],
    eventTrend: [],
    featureTrend: [],
    adTypeTrend: [],
    recent: [],
};
const EMPTY_REVIEW_CTA_STATS = {
    totalEvents: 0,
    shown: 0,
    dismissed: 0,
    reviewClicked: 0,
    uniqueDevices: 0,
    uniqueGroups: 0,
    clickRate: 0,
    dismissRate: 0,
    byEventType: [],
    bySource: [],
    sourceBreakdown: [],
    eventTrend: [],
    sourceTrend: [],
};
const TABS = ['한눈에', '홈 안내', '기능 사용', '구매·저장', '운영 참고', '건의', '리뷰', '약 후보', '도구'];
const AD_PERIOD_OPTIONS = [
    { value: 'daily', label: '일간', days: 30, summaryDays: 1, helper: '최근 30일 일별', summaryHelper: '오늘', granularity: 'daily' },
    { value: 'weekly', label: '주간', days: 78, summaryDays: 7, helper: '최근 12주 주별', summaryHelper: '최근 7일', granularity: 'weekly' },
    { value: 'monthly', label: '월간', days: 335, summaryDays: 30, helper: '최근 12개월 월별', summaryHelper: '최근 30일', granularity: 'monthly' },
];

function formatDate(dateStr) {
    if (!dateStr) return '-';
    const [year, month, day] = String(dateStr).split('-').map(Number);
    if (!year || !month || !day) return dateStr;
    return `${month}/${day}`;
}

function toDateStr(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function formatDateTime(iso) {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '-';
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : value;
}

function formatPercent(value) {
    const n = Number(value || 0);
    return `${Number.isFinite(n) ? n.toFixed(1) : '0.0'}%`;
}

function formatDurationSec(value) {
    const seconds = Number(value || 0);
    if (!Number.isFinite(seconds) || seconds <= 0) return '0초';
    if (seconds < 60) return `${Math.round(seconds)}초`;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    if (minutes < 60) return rest > 0 ? `${minutes}분 ${rest}초` : `${minutes}분`;
    const hours = Math.floor(minutes / 60);
    const minuteRest = minutes % 60;
    return minuteRest > 0 ? `${hours}시간 ${minuteRest}분` : `${hours}시간`;
}

function formatStickiness(numerator, denominator) {
    const top = Number(numerator);
    const bottom = Number(denominator);
    if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) return '-';
    return `${((top / bottom) * 100).toFixed(1)}%`;
}

function loadAppRuntimeInfo() {
    try {
        return JSON.parse(localStorage.getItem(APP_RUNTIME_INFO_KEY) || 'null');
    } catch {
        return null;
    }
}

export default function AdminPage() {
    const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem(ADMIN_KEY) === 'true');
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState(false);

    useEffect(() => {
        if (password.length < 4) {
            setAuthError(false);
            return;
        }
        let canceled = false;
        const t = setTimeout(async () => {
            sessionStorage.setItem(ADMIN_PASSWORD_KEY, password);
            try {
                await getAdminUsageSummary();
                if (canceled) return;
                sessionStorage.setItem(ADMIN_KEY, 'true');
                setAuthenticated(true);
            } catch {
                if (canceled) return;
                sessionStorage.removeItem(ADMIN_PASSWORD_KEY);
                setAuthError(true);
            }
        }, 300);
        return () => { canceled = true; clearTimeout(t); };
    }, [password]);

    if (!authenticated) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', px: 3 }}>
                <LockIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <SingleLineText sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#333', mb: 3 }}>
                    관리자 인증
                </SingleLineText>
                <TextField
                    type="password"
                    placeholder="비밀번호"
                    value={password}
                    onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '');
                        setPassword(v);
                        setAuthError(false);
                    }}
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        autoComplete: 'one-time-code',
                    }}
                    error={authError}
                    helperText={authError ? '비밀번호가 틀렸습니다' : ''}
                    autoFocus
                    sx={{
                        width: '100%', maxWidth: 280,
                        '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' },
                    }}
                />
            </Box>
        );
    }

    return <AdminDashboard />;
}

function AdminDashboard() {
    const [referenceTab, setReferenceTab] = useState(0);
    const [usage, setUsage] = useState(null);
    const [trend, setTrend] = useState([]);
    const [distribution, setDistribution] = useState(null);
    const [imageDistribution, setImageDistribution] = useState(null);
    const [notificationPatterns, setNotificationPatterns] = useState(EMPTY_NOTIFICATION_PATTERNS);
    const [todayMedicationActions, setTodayMedicationActions] = useState(EMPTY_TODAY_MEDICATION_ACTIONS);
    const [pageDurationStats, setPageDurationStats] = useState(EMPTY_PAGE_DURATION_STATS);
    const [adStats, setAdStats] = useState(EMPTY_AD_STATS);
    const [distributionError, setDistributionError] = useState(false);
    const [imageDistributionError, setImageDistributionError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [collecting, setCollecting] = useState(false);
    const [days, setDays] = useState(7);
    const [adPeriod, setAdPeriod] = useState('daily');
    const [reviewPeriod, setReviewPeriod] = useState('daily');
    const [reviewCtaStats, setReviewCtaStats] = useState(EMPTY_REVIEW_CTA_STATS);
    const [adminReviews, setAdminReviews] = useState([]);
    const [tab, setTab] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [menuAnchor, setMenuAnchor] = useState(null);
    useBackHandler(Boolean(menuAnchor), () => setMenuAnchor(null), 1300);
    const [deployInfo, setDeployInfo] = useState(null);
    const [appRuntimeInfo, setAppRuntimeInfo] = useState(loadAppRuntimeInfo);
    const [myDeviceInfo, setMyDeviceInfo] = useState(null);
    const [myEntitlements, setMyEntitlements] = useState(null);
    const [adFreeUpdating, setAdFreeUpdating] = useState(false);
    const [adFreeScope, setAdFreeScope] = useState('GROUP');
    const [currentUuid] = useState(() => localStorage.getItem(UUID_KEY) || '-');

    useEffect(() => {
        getDeploymentInfo().then(setDeployInfo).catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (event) => setAppRuntimeInfo(event.detail || loadAppRuntimeInfo());
        window.addEventListener('app-runtime-info-updated', handler);
        return () => window.removeEventListener('app-runtime-info-updated', handler);
    }, []);

    useEffect(() => {
        if (!currentUuid || currentUuid === '-') return;
        getDeviceInfo(currentUuid).then(setMyDeviceInfo).catch(() => setMyDeviceInfo(null));
        getEntitlements(currentUuid).then(setMyEntitlements).catch(() => setMyEntitlements(null));
    }, [currentUuid]);

    const refreshMyEntitlements = useCallback(async () => {
        if (!currentUuid || currentUuid === '-') return null;
        const entitlements = await getEntitlements(currentUuid);
        setMyEntitlements(entitlements);
        return entitlements;
    }, [currentUuid]);

    const handleGrantAdFree = async () => {
        if (!currentUuid || currentUuid === '-') {
            setSnackbar({ open: true, message: 'UUID가 없어요', severity: 'warning' });
            return;
        }
        setAdFreeUpdating(true);
        try {
            localStorage.removeItem(ENTITLEMENT_CACHE_KEY);
            const result = await grantAdminAdFree(currentUuid, '관리자 도구에서 수동 지급', adFreeScope);
            setMyEntitlements(result);
            setSnackbar({ open: true, message: '광고 제거를 지급했어요', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || '광고 제거 지급 실패', severity: 'error' });
        } finally {
            setAdFreeUpdating(false);
        }
    };

    const handleRevokeAdFree = async () => {
        if (!currentUuid || currentUuid === '-') {
            setSnackbar({ open: true, message: 'UUID가 없어요', severity: 'warning' });
            return;
        }
        setAdFreeUpdating(true);
        try {
            localStorage.removeItem(ENTITLEMENT_CACHE_KEY);
            const result = await revokeAdminAdFree(currentUuid);
            setMyEntitlements(result);
            await refreshMyEntitlements().catch(() => {});
            setSnackbar({ open: true, message: result.adFree ? '관리자 지급분을 회수했어요. 다른 광고 제거 권한은 유지돼요.' : '관리자 지급분을 회수했어요', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || '관리자 지급분 회수 실패', severity: 'error' });
        } finally {
            setAdFreeUpdating(false);
        }
    };

    const handleResetStoreReviewLocalState = () => {
        resetStoreReviewLocalState();
        setSnackbar({ open: true, message: '이 폰의 리뷰 CTA 상태를 완전 초기화했어요', severity: 'success' });
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const to = new Date();
            const from = new Date();
            from.setDate(from.getDate() - days + 1);
            const fromStr = toDateStr(from);
            const toStr = toDateStr(to);
            const adPeriodOption = AD_PERIOD_OPTIONS.find(option => option.value === adPeriod) || AD_PERIOD_OPTIONS[0];
            const reviewPeriodOption = AD_PERIOD_OPTIONS.find(option => option.value === reviewPeriod) || AD_PERIOD_OPTIONS[0];
            const adFrom = new Date(to);
            adFrom.setDate(adFrom.getDate() - adPeriodOption.days + 1);
            const adFromStr = toDateStr(adFrom);
            const adSummaryFrom = new Date(to);
            adSummaryFrom.setDate(adSummaryFrom.getDate() - adPeriodOption.summaryDays + 1);
            const adSummaryFromStr = toDateStr(adSummaryFrom);
            const reviewFrom = new Date(to);
            reviewFrom.setDate(reviewFrom.getDate() - reviewPeriodOption.days + 1);
            const reviewFromStr = toDateStr(reviewFrom);
            const reviewSummaryFrom = new Date(to);
            reviewSummaryFrom.setDate(reviewSummaryFrom.getDate() - reviewPeriodOption.summaryDays + 1);
            const reviewSummaryFromStr = toDateStr(reviewSummaryFrom);

            const [u, t, dist, imageDist, notifications, todayActions, pageDuration, ads, adSummary, review, reviewSummary, reviews] = await Promise.allSettled([
                getAdminUsageSummary(),
                getAdminUsageTrend(fromStr, toStr),
                getMedicationDistribution(),
                getImageRegistrationDistribution(),
                getNotificationPatterns(),
                getTodayMedicationActions(),
                getAdminPageDurationStats(fromStr, toStr),
                getAdminAdStats(adFromStr, toStr, 0, adPeriodOption.granularity),
                getAdminAdStats(adSummaryFromStr, toStr, 0, 'daily'),
                getAdminStoreReviewCtaStats(reviewFromStr, toStr, reviewPeriodOption.granularity),
                getAdminStoreReviewCtaStats(reviewSummaryFromStr, toStr, 'daily'),
                getAdminReviews(),
            ]);
            if (u.status === 'fulfilled') setUsage(u.value);
            if (t.status === 'fulfilled') setTrend(sortByDateDesc(t.value));
            if (dist.status === 'fulfilled') {
                setDistribution(dist.value);
                setDistributionError(false);
            } else {
                setDistribution(EMPTY_MEDICATION_DISTRIBUTION);
                setDistributionError(true);
            }
            if (imageDist.status === 'fulfilled') {
                setImageDistribution(imageDist.value);
                setImageDistributionError(false);
            } else {
                setImageDistribution(EMPTY_IMAGE_DISTRIBUTION);
                setImageDistributionError(true);
            }
            setNotificationPatterns(notifications.status === 'fulfilled' ? notifications.value : EMPTY_NOTIFICATION_PATTERNS);
            setTodayMedicationActions(todayActions.status === 'fulfilled' ? todayActions.value : EMPTY_TODAY_MEDICATION_ACTIONS);
            setPageDurationStats(pageDuration.status === 'fulfilled' ? pageDuration.value : EMPTY_PAGE_DURATION_STATS);
            if (ads.status === 'fulfilled') {
                const summary = adSummary.status === 'fulfilled' ? adSummary.value : ads.value;
                setAdStats({
                    ...summary,
                    trend: ads.value.trend || [],
                    requestTrend: ads.value.requestTrend || [],
                    eventTrend: ads.value.eventTrend || [],
                    featureTrend: ads.value.featureTrend || [],
                    adTypeTrend: ads.value.adTypeTrend || [],
                    summaryScopeLabel: adPeriodOption.summaryHelper,
                    trendScopeLabel: adPeriodOption.helper,
                });
            } else {
                setAdStats(EMPTY_AD_STATS);
            }
            if (review.status === 'fulfilled') {
                const summary = reviewSummary.status === 'fulfilled' ? reviewSummary.value : review.value;
                setReviewCtaStats({
                    ...summary,
                    eventTrend: review.value.eventTrend || [],
                    sourceTrend: review.value.sourceTrend || [],
                    trendScopeLabel: reviewPeriodOption.helper,
                    summaryScopeLabel: reviewPeriodOption.summaryHelper,
                });
            } else {
                setReviewCtaStats(EMPTY_REVIEW_CTA_STATS);
            }
            setAdminReviews(reviews.status === 'fulfilled' ? reviews.value : []);
        } catch (err) {
            console.error('Failed to load admin stats:', err);
        } finally {
            setLoading(false);
        }
    }, [days, adPeriod, reviewPeriod]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCollect = async () => {
        setCollecting(true);
        try {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            await collectStats(toDateStr(yesterday));
            setSnackbar({ open: true, message: '어제 통계 수집 완료', severity: 'success' });
            fetchData();
        } catch {
            setSnackbar({ open: true, message: '수집 실패', severity: 'error' });
        } finally {
            setCollecting(false);
        }
    };

    const handleCopyUuid = async () => {
        if (!currentUuid || currentUuid === '-') {
            setSnackbar({ open: true, message: 'UUID가 없어요', severity: 'warning' });
            return;
        }
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(currentUuid);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = currentUuid;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.top = '-1000px';
                textarea.style.left = '-1000px';
                document.body.appendChild(textarea);
                textarea.select();
                const copied = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (!copied) throw new Error('copy failed');
            }
            setSnackbar({ open: true, message: 'UUID를 복사했어요', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: '복사를 지원하지 않는 환경이에요', severity: 'info' });
        }
    };

    if (loading) {
        return (
            <PageLoading />
        );
    }

    return (
        <Box sx={{ background: '#f5f5f5', pb: 2 }}>
            <PageHeader title="서비스 현황" maxWidth={1120} back rightAction={
                <>
                    <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: '#666' }}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        disableScrollLock
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        PaperProps={{ sx: { borderRadius: 3, minWidth: 180, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
                    >
                        <MenuItem onClick={() => {
                            setMenuAnchor(null);
                            localStorage.removeItem('onboarding_done');
                            setSnackbar({ open: true, message: '온보딩 초기화 완료 · 홈으로 돌아가면 다시 볼 수 있어요', severity: 'success' });
                        }}>
                            <ListItemIcon><ReplayIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                            <ListItemText primary="온보딩 다시 보기" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                        </MenuItem>
                        <MenuItem onClick={() => {
                            setMenuAnchor(null);
                            sessionStorage.removeItem(ADMIN_KEY);
                            sessionStorage.removeItem(ADMIN_PASSWORD_KEY);
                            window.location.reload();
                        }}>
                            <ListItemIcon><LogoutIcon sx={{ fontSize: 20 }} /></ListItemIcon>
                            <ListItemText primary="로그아웃" primaryTypographyProps={{ fontSize: '0.95rem' }} />
                        </MenuItem>
                    </Menu>
                </>
            } />

            <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #eee', mt: 1 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons={false}
                    sx={{
                        px: 1,
                        minHeight: 44,
                        '& .MuiTab-root': { minHeight: 44, fontWeight: 700, color: '#999', px: 1.5 },
                        '& .Mui-selected': { color: '#5a4a00 !important' },
                        '& .MuiTabs-indicator': { bgcolor: '#ffe566', height: 3, borderRadius: 3 },
                    }}
                >
                    {TABS.map(label => <Tab key={label} label={label} />)}
                </Tabs>
            </Box>

            <Box sx={{ px: { xs: 2, sm: 4 }, mt: 3, '& > *:last-child': { mb: 0 } }}>
                {tab === 3 && <AdminPurchaseCustomers />}
                {tab < 4 && <ProductAnalyticsDashboard view={tab} LineChartComponent={LineChart} />}
                {tab === 4 && <>
                    <Typography sx={{ color: '#9a3412', fontSize: '0.85rem', mb: 2 }}>기존 운영 참고 통계입니다. 테스트가 포함될 수 있고 현재 설정·과거 이력이 섞여 있어 고객 전환율이나 성장 판단에 사용하지 않습니다.</Typography>
                    <Tabs value={referenceTab} onChange={(_, value) => setReferenceTab(value)} variant="scrollable" scrollButtons={false}>
                        {['API 활동', '현재 상태 비교', '설정 현황', '과거 유지', '광고 운영'].map(label => <Tab key={label} label={label} />)}
                    </Tabs>
                {[0, 3].includes(referenceTab) && <PeriodSelector days={days} setDays={setDays} />}
                {referenceTab === 0 && <OverviewTab usage={usage} trend={trend} todayMedicationActions={todayMedicationActions} />}
                {referenceTab === 1 && <ConversionTab usage={usage} />}
                {referenceTab === 2 && (
                    <BehaviorTab
                        usage={usage}
                        distribution={distribution || EMPTY_MEDICATION_DISTRIBUTION}
                        imageDistribution={imageDistribution || EMPTY_IMAGE_DISTRIBUTION}
                        notificationPatterns={notificationPatterns || EMPTY_NOTIFICATION_PATTERNS}
                        distributionError={distributionError}
                        imageDistributionError={imageDistributionError}
                    />
                )}
                {referenceTab === 3 && <RetentionTab usage={usage} trend={trend} pageDurationStats={pageDurationStats} />}
                {referenceTab === 4 && <AdsTab adStats={adStats} adPeriod={adPeriod} setAdPeriod={setAdPeriod} />}
                </>}
                {tab === 5 && <AdminReviewsTab reviews={adminReviews} setReviews={setAdminReviews} setSnackbar={setSnackbar} />}
                {tab === 6 && <ReviewCtaTab reviewCtaStats={reviewCtaStats} reviewPeriod={reviewPeriod} setReviewPeriod={setReviewPeriod} />}
                {tab === 7 && <MedicineCandidatesTab setSnackbar={setSnackbar} />}
                {tab === 8 && (
                    <ToolsTab
                        currentUuid={currentUuid}
                        handleCopyUuid={handleCopyUuid}
                        handleCollect={handleCollect}
                        collecting={collecting}
                        deployInfo={deployInfo}
                        appRuntimeInfo={appRuntimeInfo}
                        myDeviceInfo={myDeviceInfo}
                        myEntitlements={myEntitlements}
                        adFreeUpdating={adFreeUpdating}
                        adFreeScope={adFreeScope}
                        setAdFreeScope={setAdFreeScope}
                        handleGrantAdFree={handleGrantAdFree}
                        handleRevokeAdFree={handleRevokeAdFree}
                        handleResetStoreReviewLocalState={handleResetStoreReviewLocalState}
                    />
                )}
            </Box>

            <AppToast
                open={snackbar.open}
                autoHideDuration={2000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}

function OverviewTab({ usage, trend, todayMedicationActions }) {
    const actionUsers = todayMedicationActions?.actionUsers ?? todayMedicationActions?.totalGroups;
    const dauMauTrend = trend
        .map(row => ({
            ...row,
            dauMauRate: row.dauMauRate ?? row.mauDauRatio,
        }))
        .filter(row => Number(row.dauMauRate || 0) > 0);
    return (
        <>
            <SectionTitle title="서비스가 살아 있나" desc="설치, 활성, 휴면을 빠르게 확인해요" />
            <DefinitionCard
                title="읽는 법"
                body="위 카드 숫자는 지금 시점의 실시간 스냅샷입니다. 월간 사용자 중 오늘 사용자는 최근 30일에 들어온 기기 중 오늘도 들어온 기기 비율이고, 복약 액션 사용자는 오늘 약에 복용 완료·건너뜀·나중에 알림을 한 유니크 사용자 수입니다."
            />
            <KpiGrid>
                <SummaryCard label="현재 설치 기기" value={formatNumber(usage?.totalDevices)} color="#111827" helper="deleted=false 기준 활성 기기" />
                <SummaryCard label="DAU" value={formatNumber(usage?.dau)} color="#2563eb" helper="오늘 API 활동이 기록된 기기" />
                <SummaryCard label="오늘 복약 액션 사용자" value={formatNumber(actionUsers)} color="#16a34a" helper="오늘 복용 완료·건너뜀·나중에 알림을 한 유니크 사용자" />
                <SummaryCard label="WAU" value={formatNumber(usage?.wau)} color="#0f766e" helper="최근 7일 1회 이상 접속한 기기" />
                <SummaryCard label="MAU" value={formatNumber(usage?.mau)} color="#7c3aed" helper="최근 30일 1회 이상 접속한 기기" />
                <SummaryCard label="월간 사용자 중 오늘 사용자" value={formatStickiness(usage?.dau, usage?.mau)} color="#db2777" helper="최근 30일 사용자 중 오늘도 들어온 비율" />
                <SummaryCard label="7일 휴면" value={formatNumber(usage?.dormantDevices7Days)} color="#ef4444" helper="7일 이상 접속 기록 없는 기기" />
            </KpiGrid>
            <TodayMedicationActionCard actions={todayMedicationActions || EMPTY_TODAY_MEDICATION_ACTIONS} />

            <LineChart
                title="DAU 추이"
                subtitle="과거 날짜는 확정값, 오늘은 실시간 잠정치입니다"
                data={trend}
                series={[{ key: 'dau', label: 'DAU', color: '#2563eb' }]}
            />
            <LineChart
                title="월간 사용자 중 오늘 사용자 추이"
                subtitle="최근 30일에 들어온 기기 중 해당 날짜에도 들어온 기기 비율입니다. 배포 이후 수집분부터 표시됩니다"
                data={dauMauTrend}
                series={[{ key: 'dauMauRate', label: '월간 중 오늘', color: '#db2777' }]}
                valueFormatter={(value) => `${Number(value || 0).toFixed(1)}%`}
                labelZeroValues={false}
            />
            <LineChart
                title="복약 액션 사용자 추이"
                subtitle="복용 완료, 건너뜀, 나중에 알림을 한 유니크 사용자 수입니다"
                data={trend}
                series={[{ key: 'medicationActionUsers', label: '복약 액션', color: '#16a34a' }]}
            />
        </>
    );
}

function ConversionTab({ usage }) {
    const steps = [
        { label: '설치 그룹', value: usage?.totalGroups || 0, color: '#111827' },
        { label: '약 등록 그룹', value: usage?.medicationGroups || 0, color: '#2563eb', rate: usage?.installToMedicationRate },
        { label: '첫 복용 처리 그룹', value: usage?.everTakenGroups || 0, color: '#16a34a', rate: usage?.medicationToFirstTakenRate },
        { label: '최근 7일 복용 완료 기록 그룹', value: usage?.takenGroups7Days || 0, color: '#f59e0b', rate: usage?.recentCoreUsageRate7Days },
    ];
    const max = Math.max(...steps.map(s => Number(s.value || 0)), 1);

    return (
        <>
            <SectionTitle title="어디서 막히나" desc="설치 그룹이 약 등록 그룹, 복용 처리 그룹으로 이어지는지 봅니다" />
            <DefinitionCard
                title="기준"
                body="이 탭은 현재 살아 있는 설치 그룹을 기준으로 봅니다. 설치 후 삭제된 그룹이나 데이터가 지워진 그룹은 퍼널에서 빠집니다."
            />
            <CardBox>
                <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a', mb: 2 }}>핵심 전환 퍼널</SingleLineText>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                    {steps.map((step, i) => (
                        <Box key={step.label}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.6 }}>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#333' }}>{step.label}</Typography>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: step.color }}>
                                    {formatNumber(step.value)}{i > 0 && <span style={{ color: '#999', fontWeight: 600 }}> · {formatPercent(step.rate)}</span>}
                                </Typography>
                            </Box>
                            <Box sx={{ height: 14, bgcolor: '#f1f5f9', borderRadius: 7, overflow: 'hidden' }}>
                                <Box sx={{ width: `${(Number(step.value || 0) / max) * 100}%`, height: '100%', bgcolor: step.color, borderRadius: 7 }} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </CardBox>
            <KpiGrid>
                <SummaryCard label="약 등록 전환" value={formatPercent(usage?.installToMedicationRate)} color="#2563eb" helper="약 등록 그룹 / 설치 그룹" />
                <SummaryCard label="첫 복용 전환" value={formatPercent(usage?.medicationToFirstTakenRate)} color="#16a34a" helper="첫 복용 처리 그룹 / 약 등록 그룹" />
                <SummaryCard label="최근 7일 핵심 사용" value={formatPercent(usage?.recentCoreUsageRate7Days)} color="#f59e0b" helper="최근 7일 날짜에 복용 완료 기록 있는 그룹 / 약 등록 그룹" />
                <SummaryCard label="등록만 한 그룹" value={formatNumber(usage?.medicationNoTakenGroups)} color="#ef4444" helper="약 등록 그룹 중 복용 처리 0회" />
                <SummaryCard label="등록 전 이탈 그룹" value={formatNumber(usage?.installedNoMedicationGroups)} color="#9ca3af" helper="설치 그룹 중 약 등록 0개" />
                <SummaryCard label="최근 30일 핵심 사용" value={formatPercent(usage?.recentCoreUsageRate30Days)} color="#7c3aed" helper="최근 30일 날짜에 복용 완료 기록 있는 그룹 / 약 등록 그룹" />
            </KpiGrid>
        </>
    );
}

function BehaviorTab({ usage, distribution, imageDistribution, notificationPatterns, distributionError, imageDistributionError }) {
    return (
        <>
            <SectionTitle title="어떻게 쓰나" desc="약, 재고, 알림 방식, 사진, 가족 알림, 기기 연결, PRN의 실제 사용을 봅니다" />
            <DefinitionCard
                title="기준"
                body="이 탭 숫자는 대부분 실시간 스냅샷입니다. 그룹 단위와 기기 단위가 섞여 있으니 각 카드의 보조 문구를 같이 읽어야 해석이 맞습니다."
            />
            <KpiGrid>
                <SummaryCard label="활성 약" value={formatNumber(usage?.activeMedications)} color="#2563eb" helper="일시정지 제외 활성 약 개수" />
                <SummaryCard label="PRN 그룹" value={formatNumber(usage?.prnGroups)} color="#0ea5e9" helper={`약 등록 그룹 중 PRN 사용 ${formatPercent(usage?.prnRate)}`} />
                <SummaryCard label="재고 관리 그룹" value={formatNumber(usage?.stockManagedGroups)} color="#059669" helper={`약 등록 그룹 중 재고 관리 ${formatPercent(usage?.stockManagementRate)}`} />
                <SummaryCard label="가족 약 알림 그룹" value={formatNumber(usage?.guardianLinkedGroups)} color="#ec4899" helper={`보호받는 가족 그룹 ${formatNumber(usage?.guardianLinkedGroups)}개 · 활성 링크 ${formatNumber(usage?.guardianLinks)}건`} />
                <SummaryCard label="다른 기기 연결 그룹" value={formatNumber(usage?.multiDeviceGroups)} color="#7c3aed" helper={`기기 2대 이상 그룹 · 설치 그룹 중 ${formatPercent(usage?.multiDeviceRate)}`} />
                <SummaryCard label="재고 부족 약" value={formatNumber(usage?.lowStockMedications)} color="#f59e0b" helper={`다 떨어진 약 ${formatNumber(usage?.outOfStockMedications)}개 포함`} />
            </KpiGrid>
            <StockManagementCard usage={usage} />
            <NotificationPatternCard patterns={notificationPatterns || EMPTY_NOTIFICATION_PATTERNS} />
            <DistributionCard
                title="인당 약 등록 분포"
                dist={distribution}
                avgKey="avgMedsPerGroup"
                maxKey="maxMedsPerGroup"
                color="#2563eb"
                error={distributionError}
            />
            <DistributionCard
                title="인당 이미지 등록 분포"
                dist={imageDistribution}
                avgKey="avgImagesPerGroup"
                maxKey="maxImagesPerGroup"
                color="#0ea5e9"
                error={imageDistributionError}
            />
            <FeatureUsageCard usage={usage} />
        </>
    );
}

function StockManagementCard({ usage }) {
    const managedGroups = Number(usage?.stockManagedGroups || 0);
    const managedMedications = Number(usage?.stockManagedMedications || 0);
    const active7Days = Number(usage?.stockManagedActiveGroups7Days || 0);
    const active30Days = Number(usage?.stockManagedActiveGroups30Days || 0);
    const lowStock = Number(usage?.lowStockMedications || 0);
    const outOfStock = Number(usage?.outOfStockMedications || 0);
    return (
        <CardBox>
            <Box sx={{ mb: 1.5 }}>
                <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>재고 관리</SingleLineText>
                <Typography sx={{ fontSize: '0.82rem', color: '#888', mt: 0.25, lineHeight: 1.45 }}>
                    총 재고를 설정한 활성 약과 그 사용 그룹의 최근 접속 상태입니다
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.2 }}>
                <MetricItem label="사용 그룹" value={`${formatNumber(managedGroups)}그룹`} color="#059669" />
                <MetricItem label="관리 중인 약" value={`${formatNumber(managedMedications)}개`} color="#2563eb" />
                <MetricItem label="최근 7일 접속" value={`${formatNumber(active7Days)}그룹`} color="#16a34a" />
                <MetricItem label="최근 30일 접속" value={`${formatNumber(active30Days)}그룹`} color="#0f766e" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box sx={{ p: 1.2, bgcolor: lowStock > 0 ? '#fffbeb' : '#f8fafc', border: `1px solid ${lowStock > 0 ? '#fef3c7' : '#eef2f7'}`, borderRadius: 1 }}>
                    <SingleLineText sx={{ fontSize: '0.78rem', color: '#888', fontWeight: 800, mb: 0.3 }}>부족 알림 대상</SingleLineText>
                    <Typography sx={{ fontSize: '1.15rem', color: lowStock > 0 ? '#f59e0b' : '#64748b', fontWeight: 900, lineHeight: 1.15 }}>
                        {formatNumber(lowStock)}개
                    </Typography>
                    <SingleLineText sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.4, lineHeight: 1.35 }}>
                        남은 수량이 알림 기준 이하
                    </SingleLineText>
                </Box>
                <Box sx={{ p: 1.2, bgcolor: outOfStock > 0 ? '#fef2f2' : '#f8fafc', border: `1px solid ${outOfStock > 0 ? '#fee2e2' : '#eef2f7'}`, borderRadius: 1 }}>
                    <SingleLineText sx={{ fontSize: '0.78rem', color: '#888', fontWeight: 800, mb: 0.3 }}>소진된 약</SingleLineText>
                    <Typography sx={{ fontSize: '1.15rem', color: outOfStock > 0 ? '#ef4444' : '#64748b', fontWeight: 900, lineHeight: 1.15 }}>
                        {formatNumber(outOfStock)}개
                    </Typography>
                    <SingleLineText sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.4, lineHeight: 1.35 }}>
                        남은 수량 0개 이하
                    </SingleLineText>
                </Box>
            </Box>
        </CardBox>
    );
}

function RetentionTab({ usage, trend, pageDurationStats }) {
    const today = toDateStr(new Date());
    const retentionTrend = trend.filter(row => row.date !== today);
    return (
        <>
            <SectionTitle title="계속 쓰나" desc="설치 후 다시 돌아오는지와 이미 떠난 사용자를 같이 봅니다" />
            <DefinitionCard
                title="재방문 읽는 법"
                body="D1은 전날 설치한 기기 중 오늘 다시 접속한 비율, D7은 7일 전 설치한 기기 중 오늘 다시 접속한 비율, D30은 30일 전 설치한 기기 중 오늘 다시 접속한 비율입니다. 분모에는 이후 삭제된 기기도 포함해서 과대평가를 막습니다."
            />
            <DefinitionCard
                title="스냅샷과 추이"
                body="위 카드 숫자는 지금 시점의 실시간 상태입니다. 아래 리텐션 추이는 날짜별로 저장된 확정 데이터라 당일 값은 낮거나 흔들릴 수 있고, 어제까지의 흐름을 보는 용도로 해석하는 게 좋습니다."
            />
            <KpiGrid>
                <SummaryCard label="7일 휴면 기기" value={formatNumber(usage?.dormantDevices7Days)} color="#ef4444" helper="최근 7일 접속 기록 없는 기기" />
                <SummaryCard label="30일 휴면 기기" value={formatNumber(usage?.dormantDevices30Days)} color="#991b1b" helper="최근 30일 접속 기록 없는 기기" />
                <SummaryCard label="약 등록 후 휴면" value={formatNumber(usage?.medicationDormantGroups7Days)} color="#f59e0b" helper="약 등록 그룹 중 최근 7일 미접속" />
                <SummaryCard label="복용 후 휴면" value={formatNumber(usage?.takenDormantGroups7Days)} color="#7c3aed" helper="복용 경험 그룹 중 최근 7일 날짜에 복용 완료 기록 없음" />
                <SummaryCard label="삭제 처리 기기" value={formatNumber(usage?.deletedDevices)} color="#6b7280" helper="토큰 만료/내 데이터 삭제 등으로 deleted=true" />
                <SummaryCard label="최근 활성 그룹" value={formatNumber(usage?.activeGroups7Days)} color="#16a34a" helper="최근 7일 1회 이상 접속한 그룹" />
            </KpiGrid>
            <PageDurationCard stats={pageDurationStats || EMPTY_PAGE_DURATION_STATS} />
            <MedicationStreakCard streaks={usage?.medicationStreaks} />
            <LineChart
                title="설치 후 재방문 추이"
                subtitle="각 날짜마다 1일 전, 7일 전, 30일 전에 설치한 기기들이 그날 다시 접속한 비율입니다. 오늘은 제외합니다"
                data={retentionTrend}
                percent
                series={[
                    { key: 'retentionD1', label: 'D1', color: '#2563eb' },
                    { key: 'retentionD7', label: 'D7', color: '#16a34a' },
                    { key: 'retentionD30', label: 'D30', color: '#7c3aed' },
                ]}
            />
            <DefinitionCard
                title="삭제 해석"
                body="삭제 처리 기기는 현재 deleted=true 상태의 실시간 스냅샷입니다. 재설치나 복구로 과거 의미가 흔들릴 수 있어, 날짜별 추이 차트에서는 제외했습니다."
            />
        </>
    );
}

function PageDurationCard({ stats }) {
    const rows = stats?.byPage || [];
    const maxTotal = Math.max(...rows.map(row => Number(row.totalDurationSec || 0)), 1);

    return (
        <CardBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box>
                    <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>페이지별 체류시간</SingleLineText>
                    <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.2, lineHeight: 1.45 }}>
                        선택 기간에 사용자가 각 화면에서 머문 평균 시간입니다
                    </SingleLineText>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#999', fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {formatNumber(stats?.uniqueDevices)}기기
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.4 }}>
                <MetricItem label="전체 평균" value={formatDurationSec(stats?.avgDurationSec)} color="#2563eb" />
                <MetricItem label="측정 건수" value={`${formatNumber(stats?.totalEvents)}건`} color="#7c3aed" />
            </Box>
            {rows.length === 0 ? (
                <SingleLineText sx={{ fontSize: '0.86rem', color: '#999' }}>선택 기간에 체류시간 데이터가 없어요</SingleLineText>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rows.slice(0, 10).map(row => (
                        <Box key={row.pageRoute} sx={{ p: 1.2, bgcolor: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.8 }}>
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 900, color: '#333', minWidth: 0, overflowWrap: 'anywhere' }}>
                                    {row.pageRoute}
                                </Typography>
                                <Typography sx={{ fontSize: '0.84rem', fontWeight: 900, color: '#2563eb', whiteSpace: 'nowrap' }}>
                                    평균 {formatDurationSec(row.avgDurationSec)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 88px', alignItems: 'center', gap: 1, mb: 0.6 }}>
                                <Box sx={{ height: 10, bgcolor: '#eef2ff', borderRadius: 5, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${(Number(row.totalDurationSec || 0) / maxTotal) * 100}%`, bgcolor: '#7c3aed', borderRadius: 5 }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 800, textAlign: 'right' }}>
                                    총 {formatDurationSec(row.totalDurationSec)}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.74rem', color: '#999', lineHeight: 1.35 }}>
                                {formatNumber(row.events)}건 · {formatNumber(row.uniqueDevices)}기기 · {formatNumber(row.uniqueGroups)}그룹
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </CardBox>
    );
}

function MedicationStreakCard({ streaks }) {
    const thresholds = streaks?.thresholds || [3, 7, 14, 30].map(days => ({ days, groups: 0, rate: 0 }));
    return (
        <CardBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box>
                    <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>연속 복약 처리</SingleLineText>
                    <Typography sx={{ fontSize: '0.82rem', color: '#888', mt: 0.2, lineHeight: 1.45 }}>
                        어제까지 복용 완료·건너뜀·나중에 알림 중 하나라도 처리한 연속 일수
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#999', fontWeight: 800, whiteSpace: 'nowrap' }}>
                    기준 {formatNumber(streaks?.totalGroups)}그룹
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.2 }}>
                <MetricItem label="평균 연속 처리일" value={`${formatNumber(streaks?.avgCurrentStreakDays)}일`} color="#2563eb" />
                <MetricItem label="최장 연속 처리일" value={`${formatNumber(streaks?.maxCurrentStreakDays)}일`} color="#7c3aed" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {thresholds.map((item) => (
                    <Box key={item.days} sx={{ p: 1.2, bgcolor: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 1 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#888', fontWeight: 800, mb: 0.3 }}>
                            {item.days}일 이상
                        </Typography>
                        <Typography sx={{ fontSize: '1.15rem', color: '#16a34a', fontWeight: 900, lineHeight: 1.15 }}>
                            {formatPercent(item.rate)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.4, lineHeight: 1.35 }}>
                            {formatNumber(item.groups)}그룹
                        </Typography>
                    </Box>
                ))}
            </Box>
        </CardBox>
    );
}

function AdsTab({ adStats, adPeriod, setAdPeriod }) {
    const stats = adStats || EMPTY_AD_STATS;
    const eventCounts = Object.fromEntries((stats.byEventType || []).map(row => [row.eventType, Number(row.count || 0)]));
    const requestCount = Number(stats.requestEvents ?? eventCounts.request ?? 0);
    const showCount = Number(stats.showEvents ?? eventCounts.show ?? 0);
    const failCount = Number(stats.displayFailures ?? ((eventCounts.unavailable || 0) + (eventCounts.error || 0)));
    const preloadFailCount = Number(stats.preloadFailures ?? (eventCounts.preload_error || 0));
    const displayRate = requestCount > 0 ? (showCount / requestCount) * 100 : 0;
    const periodOption = AD_PERIOD_OPTIONS.find(option => option.value === adPeriod) || AD_PERIOD_OPTIONS[0];
    const periodLabel = stats.trendScopeLabel || periodOption.helper;
    const summaryLabel = stats.summaryScopeLabel || periodOption.summaryHelper;
    const featureSeries = [
        { key: 'medication_action', label: AD_FEATURE_LABELS.medication_action, color: '#2563eb' },
        { key: 'monthly_recap', label: AD_FEATURE_LABELS.monthly_recap, color: '#7c3aed' },
        { key: 'ai_chat_reward', label: AD_FEATURE_LABELS.ai_chat_reward, color: '#0ea5e9' },
        { key: 'smart_suggest_reward', label: AD_FEATURE_LABELS.smart_suggest_reward, color: '#10b981' },
        { key: 'medication_slot_reward', label: AD_FEATURE_LABELS.medication_slot_reward, color: '#f97316' },
        { key: 'guardian_nudge_reward', label: AD_FEATURE_LABELS.guardian_nudge_reward, color: '#ec4899' },
    ];
    const adTypeSeries = [
        { key: 'interstitial', label: AD_TYPE_LABELS.interstitial, color: '#16a34a' },
        { key: 'rewarded', label: AD_TYPE_LABELS.rewarded, color: '#f59e0b' },
    ];
    return (
        <>
            <SectionTitle title="광고" desc="웹 요청 지표와 네이티브 표시 상태를 분리해서 봅니다" />
            <AdPeriodSelector value={adPeriod} onChange={setAdPeriod} />
            <KpiGrid>
                <SummaryCard label="웹 광고 요청" value={formatNumber(requestCount)} color="#111827" helper={`${summaryLabel} 웹 기능이 광고 실행을 요청한 횟수`} />
                <SummaryCard label="광고 요청 사용자" value={formatNumber(stats.uniqueGroups || 0)} color="#7c3aed" helper={`${summaryLabel} 광고를 요청한 유니크 사용자`} />
                <SummaryCard label="네이티브 표시" value={formatNumber(showCount)} color="#16a34a" helper={`${summaryLabel} 네이티브 광고가 실제 표시 시작한 횟수`} />
                <SummaryCard label="요청 대비 표시율" value={formatPercent(displayRate)} color="#2563eb" helper={`${summaryLabel} 웹 요청 중 show까지 간 비율`} />
                <SummaryCard label="표시 시도 실패" value={formatNumber(failCount)} color="#ef4444" helper={`${summaryLabel} 사용자 요청 후 unavailable/error로 끝난 횟수`} />
                <SummaryCard label="Preload 실패" value={formatNumber(preloadFailCount)} color="#f59e0b" helper={`${summaryLabel} 사용자 요청 전 광고 준비 실패`} />
            </KpiGrid>
            <LineChart
                title="광고 호출 추이"
                subtitle={`${periodLabel} · 웹 요청과 네이티브 결과를 같이 봅니다`}
                data={sortByDateDesc(stats.eventTrend || stats.requestTrend || [])}
                series={[
                    { key: 'requests', label: '요청', color: '#2563eb' },
                    { key: 'shows', label: '표시', color: '#16a34a' },
                    { key: 'displayFailures', label: '표시 실패', color: '#ef4444' },
                    { key: 'preloadFailures', label: '준비 실패', color: '#f59e0b' },
                ]}
            />
            <LineChart
                title="기능별 광고 호출 추이"
                subtitle={`${periodLabel} · 웹 request 기준입니다`}
                data={sortByDateDesc(stats.featureTrend || [])}
                series={featureSeries}
                labelZeroValues={false}
            />
            <LineChart
                title="광고 종류별 호출 추이"
                subtitle={`${periodLabel} · 전면/보상형 request 기준입니다`}
                data={sortByDateDesc(stats.adTypeTrend || [])}
                series={adTypeSeries}
                labelZeroValues={false}
            />
            <CountListCard title="어떤 기능에서 광고를 실행했나" rows={stats.byFeature || []} labelKey="feature" labelMap={AD_FEATURE_LABELS} color="#2563eb" suffix="건" />
            <CountListCard title="어떤 광고를 실행했나" rows={stats.byAdType || []} labelKey="adType" labelMap={AD_TYPE_LABELS} color="#16a34a" suffix="건" />
            <CountListCard title="네이티브 표시 상태" rows={stats.nativeByEventType || []} labelKey="eventType" labelMap={AD_EVENT_LABELS} color="#7c3aed" suffix="건" />
            <FailureReasonsCard rows={stats.failureReasons || []} />
        </>
    );
}

const REVIEW_CATEGORY_LABELS = {
    IMPROVEMENT: '개선 제안',
    FEATURE: '기능 요청',
    BUG: '버그 신고',
};

function getReviewReplies(review) {
    if (Array.isArray(review?.replies) && review.replies.length > 0) return review.replies;
    if (review?.adminReply) {
        return [{
            id: `legacy-${review.id}`,
            content: review.adminReply,
            userReadAt: review.userReadAt,
            createdAt: review.adminRepliedAt,
            legacy: true,
        }];
    }
    return [];
}

function reviewReplyReadCounts(review) {
    return getReviewReplies(review).reduce((counts, reply) => {
        if (reply.userReadAt) counts.read += 1;
        else counts.unread += 1;
        return counts;
    }, { read: 0, unread: 0 });
}

function readStatusLabel(readAt) {
    return readAt ? '읽음' : '안읽음';
}

function readStatusChipSx(readAt) {
    return {
        px: 1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: readAt ? '#f1f5f9' : '#fff7cc',
        color: readAt ? '#64748b' : '#8a7000',
        fontSize: 12,
        fontWeight: 900,
        whiteSpace: 'nowrap',
    };
}

function reviewReplyDraftKey(review, reply) {
    return `${review.id}:reply:${reply.id}`;
}

function reviewDeviceGroupKey(review) {
    return review.deviceUuid || `unknown:${review.id}`;
}

function shortDeviceUuid(uuid) {
    if (!uuid) return '기기 정보 없음';
    if (uuid.length <= 14) return uuid;
    return `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
}

function reviewPreviewText(review) {
    return (review?.content || '').replace(/\s+/g, ' ').trim();
}

function AdminReviewsTab({ reviews, setReviews, setSnackbar }) {
    const [replyDrafts, setReplyDrafts] = useState({});
    const [hiddenMode, setHiddenMode] = useState(false);
    const [search, setSearch] = useState('');
    const desktopReviews = useMediaQuery('(min-width:960px)');
    const [reviewPanel, setReviewPanel] = useState('conversation');
    const [onlyUnanswered, setOnlyUnanswered] = useState(false);
    const [hiddenReviews, setHiddenReviews] = useState([]);
    const [hiddenLoading, setHiddenLoading] = useState(false);
    const [expandedDeviceKey, setExpandedDeviceKey] = useState(null);
    const [expandedReviewId, setExpandedReviewId] = useState(null);
    const [editingReplyKey, setEditingReplyKey] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [visibleCount, setVisibleCount] = useState(5);
    const [scrollTick, setScrollTick] = useState(0);
    const scrollRafRef = useRef(null);
    const rows = hiddenMode ? hiddenReviews : (reviews || []);
    const groupedRows = useMemo(() => {
        const groupMap = new Map();
        sortAdminReviewSubmissions(rows).forEach((review) => {
            const key = reviewDeviceGroupKey(review);
            const group = groupMap.get(key) || {
                key,
                deviceUuid: review.deviceUuid,
                deviceLastActiveAt: review.deviceLastActiveAt,
                devicePlatform: review.devicePlatform,
                deviceAppVersion: review.deviceAppVersion,
                reviews: [],
            };
            group.reviews.push(review);
            if (!group.deviceLastActiveAt || (review.deviceLastActiveAt && new Date(review.deviceLastActiveAt) > new Date(group.deviceLastActiveAt))) {
                group.deviceLastActiveAt = review.deviceLastActiveAt;
            }
            groupMap.set(key, group);
        });
        return Array.from(groupMap.values())
            .map(group => ({
                ...group,
                reviewCount: group.reviews.length,
                latestReview: group.reviews[0],
                unansweredCount: group.reviews.filter(row => getReviewReplies(row).length === 0).length,
                unreadCount: group.reviews.reduce((sum, row) => sum + reviewReplyReadCounts(row).unread, 0),
                readCount: group.reviews.reduce((sum, row) => sum + reviewReplyReadCounts(row).read, 0),
            }))
            .sort((a, b) => new Date(b.latestReview?.createdAt || 0) - new Date(a.latestReview?.createdAt || 0));
    }, [rows]);
    const filteredGroups = groupedRows.filter(group => {
        if (onlyUnanswered && group.unansweredCount === 0) return false;
        const query = search.trim().toLowerCase();
        return !query || [group.deviceUuid, ...group.reviews.map(review => `${review.id} ${review.content} ${getReviewReplies(review).map(reply => reply.content).join(' ')}`)].join(' ').toLowerCase().includes(query);
    });
    const visibleGroups = filteredGroups.slice(0, visibleCount);
    const hasMore = visibleCount < filteredGroups.length;
    useEffect(() => { setVisibleCount(5); }, [search, onlyUnanswered]);
    const unanswered = rows.filter(row => getReviewReplies(row).length === 0).length;
    const unread = rows.reduce((sum, row) => sum + reviewReplyReadCounts(row).unread, 0);

    const loadHiddenReviews = useCallback(async () => {
        setHiddenLoading(true);
        try {
            const hiddenRows = await getAdminReviews(true);
            setHiddenReviews(hiddenRows || []);
        } catch (err) {
            setHiddenReviews([]);
            setSnackbar({ open: true, message: err.response?.data?.message || '숨김 목록을 불러오지 못했어요', severity: 'error' });
        } finally {
            setHiddenLoading(false);
        }
    }, [setSnackbar]);

    useEffect(() => {
        setVisibleCount(5);
        setExpandedDeviceKey(null);
        setExpandedReviewId(null);
        setEditingReplyKey(null);
    }, [rows.length]);

    useEffect(() => {
        const handleScroll = () => {
            if (scrollRafRef.current) return;
            scrollRafRef.current = window.requestAnimationFrame(() => {
                scrollRafRef.current = null;
                setScrollTick(prev => prev + 1);
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
        };
    }, []);

    useEffect(() => {
        if (scrollTick === 0) return;
        const doc = document.documentElement;
        const scrollBottom = window.scrollY + window.innerHeight;
        const nearBottom = scrollBottom >= doc.scrollHeight - 180;
        if (!nearBottom) return;
        setVisibleCount(prev => Math.min(prev + 5, filteredGroups.length));
    }, [filteredGroups.length, scrollTick]);

    const handleReply = async (review, replyId = null) => {
        const existingReply = replyId ? getReviewReplies(review).find(reply => reply.id === replyId) : null;
        const draftKey = existingReply ? reviewReplyDraftKey(review, existingReply) : `new:${review.id}`;
        const normalizedReplyId = existingReply?.legacy ? null : replyId;
        const reply = (replyDrafts[draftKey] ?? existingReply?.content ?? '').trim();
        if (!reply) {
            setSnackbar({ open: true, message: '답변을 입력해주세요', severity: 'warning' });
            return;
        }
        setSavingId(draftKey);
        try {
            const updated = await replyAdminReview(review.id, {
                replyId: normalizedReplyId,
                reply,
                status: 'ANSWERED',
            });
            setReviews(prev => prev.map(row => (row.id === review.id ? updated : row)));
            setReplyDrafts(prev => {
                const next = { ...prev };
                delete next[draftKey];
                return next;
            });
            setEditingReplyKey(null);
            setExpandedDeviceKey(reviewDeviceGroupKey(review));
            setExpandedReviewId(review.id);
            setSnackbar({ open: true, message: replyId ? '답변을 수정했어요' : '답변을 추가했어요', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || '답변 저장 실패', severity: 'error' });
        } finally {
            setSavingId(null);
        }
    };

    const handleStartEditReply = (review, reply) => {
        const draftKey = reviewReplyDraftKey(review, reply);
        setEditingReplyKey(draftKey);
        setReplyDrafts(prev => ({ ...prev, [draftKey]: reply.content || '' }));
    };

    const handleCancelEditReply = (draftKey) => {
        setEditingReplyKey(null);
        setReplyDrafts(prev => {
            const next = { ...prev };
            delete next[draftKey];
            return next;
        });
    };

    const handleHideReview = async (review) => {
        setSavingId(`hide:${review.id}`);
        try {
            const updated = await hideAdminReview(review.id, !hiddenMode);
            if (hiddenMode) {
                setHiddenReviews(prev => prev.filter(row => row.id !== review.id));
                setReviews(prev => [updated, ...prev.filter(row => row.id !== review.id)]);
                setSnackbar({ open: true, message: '숨김을 해제했어요', severity: 'success' });
            } else {
                setReviews(prev => prev.filter(row => row.id !== review.id));
                setHiddenReviews(prev => [updated, ...prev.filter(row => row.id !== review.id)]);
                setSnackbar({ open: true, message: '건의를 숨겼어요', severity: 'success' });
            }
            if (expandedReviewId === review.id) setExpandedReviewId(null);
            if (!rows.some(row => row.id !== review.id && reviewDeviceGroupKey(row) === reviewDeviceGroupKey(review))) {
                setExpandedDeviceKey(null);
            }
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || '건의 숨기기 실패', severity: 'error' });
        } finally {
            setSavingId(null);
        }
    };

    const handleToggleHiddenMode = async () => {
        const nextHiddenMode = !hiddenMode;
        setHiddenMode(nextHiddenMode);
        setExpandedDeviceKey(null);
        setExpandedReviewId(null);
        setEditingReplyKey(null);
        setVisibleCount(5);
        if (nextHiddenMode) await loadHiddenReviews();
    };

    return (
        <>
            <SectionTitle title="건의함" desc="사용자의 이야기를 확인하고 답변하세요" />
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="건의 내용 · 답변 · UUID 검색" value={search} onChange={event => setSearch(event.target.value)} inputProps={{ 'aria-label': '건의 검색' }} sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' } }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Button variant={onlyUnanswered ? 'contained' : 'outlined'} aria-pressed={onlyUnanswered} onClick={() => setOnlyUnanswered(value => !value)} sx={{ minHeight: 44, borderRadius: 999, boxShadow: 'none', borderColor: '#e5e7eb', bgcolor: onlyUnanswered ? '#111827' : '#fff', color: onlyUnanswered ? '#fff' : '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: onlyUnanswered ? '#1f2937' : '#f8fafc' } }}>미답변만</Button>
                <Button
                    variant={hiddenMode ? 'contained' : 'outlined'}
                    onClick={handleToggleHiddenMode}
                    disabled={hiddenLoading}
                    sx={{
                        borderRadius: 999,
                        px: 1.6,
                        py: 0.8,
                        fontSize: 13,
                        fontWeight: 900,
                        boxShadow: 'none',
                        bgcolor: hiddenMode ? '#111827' : '#fff',
                        color: hiddenMode ? '#fff' : '#64748b',
                        borderColor: '#e5e7eb',
                        '&:hover': { bgcolor: hiddenMode ? '#1f2937' : '#f8fafc', borderColor: '#d1d5db' },
                    }}
                >
                    {hiddenLoading ? <LoadingLabel>불러오는 중</LoadingLabel> : hiddenMode ? '일반 목록 보기' : `숨김 목록${hiddenReviews.length > 0 ? ` ${hiddenReviews.length}` : ''}`}
                </Button>
            </Box>
            {hiddenMode && (
                <Box sx={{ mb: 1.5, p: 1.4, bgcolor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 3 }}>
                    <SingleLineText sx={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                        숨김 처리한 건의만 보고 있어요
                    </SingleLineText>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', mt: 0.35 }}>
                        다시 목록에 보이게 하려면 건의에서 숨김 해제를 누르세요.
                    </Typography>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>{hiddenMode ? '숨김' : '최근'} 건의 <b>{formatNumber(rows.length)}</b></Typography>
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>미답변 <b>{formatNumber(unanswered)}</b></Typography>
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>안읽은 답변 <b>{formatNumber(unread)}</b></Typography>
            </Box>
            {hiddenLoading ? (
                <CardBox>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={22} />
                    </Box>
                </CardBox>
            ) : filteredGroups.length === 0 ? (
                <CardBox>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#777' }}>
                        {rows.length > 0 ? '검색 조건에 맞는 건의가 없어요' : hiddenMode ? '숨긴 건의가 없어요' : '아직 건의가 없어요'}
                    </Typography>
                </CardBox>
            ) : (
                <>
                    {visibleGroups.map(group => {
                        const groupExpanded = expandedDeviceKey === group.key;
                        const latestPreview = reviewPreviewText(group.latestReview);
                        const toggleGroup = () => {
                            setExpandedDeviceKey(groupExpanded ? null : group.key);
                            setExpandedReviewId(groupExpanded ? null : group.latestReview.id);
                            setReviewPanel('conversation');
                        };
                        return (
                            <Box key={group.key} sx={{ overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 3, mb: 1.5 }}>
                                <Box
                                    aria-expanded={groupExpanded}
                                    aria-label={`${latestPreview || '내용 없음'}${group.reviewCount > 1 ? ` 외 ${group.reviewCount - 1}건` : ''}`}
                                    onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); toggleGroup(); } }}
                                    onClick={toggleGroup}
                                    role="button"
                                    tabIndex={0}
                                    data-tappable="true"
                                    sx={{ p: 2, cursor: 'pointer', '&:active': { bgcolor: '#f8fafc' } }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2 }}>
                                        <Typography sx={{ color: '#334155', fontSize: 14, fontWeight: 800 }}>
                                            {({ ios: 'iOS', android: 'Android', web: '웹' })[group.devicePlatform?.trim().toLowerCase()] || '기기 미확인'}
                                        </Typography>
                                        <Typography sx={{ px: 0.9, py: 0.3, borderRadius: 1.5, bgcolor: group.latestReview.purchasedAdFree === true ? '#fff3c4' : '#f1f5f9', color: group.latestReview.purchasedAdFree === true ? '#854d0e' : '#64748b', fontSize: 12, fontWeight: 800 }}>
                                            {group.latestReview.purchasedAdFree === true ? 'VIP' : group.latestReview.purchasedAdFree === false ? '일반' : 'VIP 미확인'}
                                        </Typography>
                                        <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12, color: '#94a3b8', textAlign: 'right', overflowWrap: 'anywhere' }}>
                                            {group.deviceUuid ? shortDeviceUuid(group.deviceUuid) : 'UUID 없음'}
                                        </Typography>
                                        <ChevronRightIcon sx={{ fontSize: 20, color: '#94a3b8', transform: groupExpanded ? 'rotate(90deg)' : 'none' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.8, rowGap: 0.3, mb: 0.5 }}>
                                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>앱 버전</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 700, overflowWrap: 'anywhere', minWidth: 0 }}>
                                            {group.deviceAppVersion?.trim() || '미확인'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.8, rowGap: 0.3, mb: 1.2 }}>
                                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>마지막 앱 접속</Typography>
                                        <Typography sx={{ fontSize: 13, color: '#334155', fontWeight: 700 }}>
                                            {formatDateTime(group.deviceLastActiveAt) === '-' ? '접속 기록 없음' : formatDateTime(group.deviceLastActiveAt)}
                                        </Typography>
                                    </Box>
                                    {!groupExpanded && <Typography sx={{ fontSize: 16, color: '#111827', fontWeight: 700, lineHeight: 1.65, wordBreak: 'keep-all', overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1.2 }}>
                                        {latestPreview || '내용 없음'}
                                    </Typography>}
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>{formatDateTime(group.latestReview.createdAt).slice(0, 10)} · 건의 {group.reviewCount}</Typography>
                                        {group.unansweredCount > 0 && <Typography sx={{ fontSize: 12, color: '#b45309', fontWeight: 800 }}>미답변 {group.unansweredCount}</Typography>}
                                        {group.unreadCount > 0 && <Typography sx={{ fontSize: 12, color: '#64748b' }}>답변 미열람 {group.unreadCount}</Typography>}
                                    </Box>
                                </Box>
                                {groupExpanded && !desktopReviews && <Box sx={{ display: 'flex', px: 2, gap: 2, borderTop: '1px solid #eef2f7' }}>
                                    {[['conversation', '건의·답변'], ['author', '작성자·등록 약']].map(([value, label]) => <Button key={value} aria-pressed={reviewPanel === value} onClick={() => setReviewPanel(value)} sx={{ flex: 1, minHeight: 48, borderRadius: 0, borderBottom: reviewPanel === value ? '2px solid #111827' : '2px solid transparent', fontSize: 14, fontWeight: 800, color: reviewPanel === value ? '#111827' : '#94a3b8' }}>{label}</Button>)}
                                </Box>}
                                {groupExpanded && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 320px' }, alignItems: 'start' }}>
                                {(desktopReviews || reviewPanel === 'author') && <Box sx={{ order: { xs: 1, md: 2 }, minWidth: 0 }}><AdminReviewAuthor reviewId={group.latestReview.id} /></Box>}
                                <Box sx={{ display: desktopReviews || reviewPanel === 'conversation' ? 'block' : 'none', order: { xs: 2, md: 1 }, minWidth: 0 }}>
                                {group.reviews.map(review => {
                        const replies = getReviewReplies(review);
                        const hasReplies = replies.length > 0;
                        const replyReadCounts = reviewReplyReadCounts(review);
                        const newReplyDraftKey = `new:${review.id}`;
                        return (
                        <Box key={review.id} sx={{ borderTop: '1px solid #f1f5f9' }}>
                            <Box
                                aria-expanded={expandedReviewId === review.id}
                                onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); setExpandedReviewId(prev => prev === review.id ? null : review.id); } }}
                                onClick={() => setExpandedReviewId(prev => prev === review.id ? null : review.id)}
                                role="button"
                                tabIndex={0}
                                data-tappable="true"
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
                                    p: 1.6, cursor: 'pointer',
                                    '&:active': { opacity: 0.72 },
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.6, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#aaa', whiteSpace: 'nowrap' }}>
                                            #{review.id}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#8a7000', whiteSpace: 'nowrap' }}>
                                            {REVIEW_CATEGORY_LABELS[review.category] || '기타'}
                                        </Typography>
                                        {review.parentReviewId && (
                                            <SingleLineText sx={{
                                                px: 0.8, py: 0.25, borderRadius: 999,
                                                bgcolor: '#f8fafc', color: '#64748b',
                                                fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap',
                                            }}>
                                                추가 의견
                                            </SingleLineText>
                                        )}
                                        <Typography sx={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>
                                            {formatDateTime(review.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{
                                        display: expandedReviewId === review.id ? 'none' : 'block', fontSize: 15, fontWeight: 800, color: '#1a1a1a',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {review.content}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
                                    <Box sx={{
                                        px: 1, py: 0.5, borderRadius: 999,
                                        bgcolor: hasReplies ? '#f1f5f9' : '#fff7ed',
                                        color: hasReplies ? '#64748b' : '#b45309',
                                        fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                                    }}>
                                        {hasReplies ? `답변 ${replies.length}` : '미답변'}
                                    </Box>
                                    {replyReadCounts.unread > 0 && (
                                        <Box sx={readStatusChipSx(false)}>
                                            안읽음{replyReadCounts.unread > 1 ? ` ${replyReadCounts.unread}` : ''}
                                        </Box>
                                    )}
                                    {replyReadCounts.read > 0 && (
                                        <Box sx={readStatusChipSx(true)}>
                                            읽음{replyReadCounts.read > 1 ? ` ${replyReadCounts.read}` : ''}
                                        </Box>
                                    )}
                                        <Button
                                            size="small"
                                            disabled={savingId === `hide:${review.id}`}
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleHideReview(review);
                                            }}
                                            sx={{
                                                minWidth: 0,
                                                px: 1,
                                                py: 0.45,
                                                borderRadius: 999,
                                                bgcolor: '#f8fafc',
                                                color: '#64748b',
                                                fontSize: 12,
                                                fontWeight: 900,
                                                '&:hover': { bgcolor: '#eef2f7' },
                                            }}
                                        >
                                            {savingId === `hide:${review.id}` ? <LoadingLabel>{hiddenMode ? '해제 중' : '숨기는 중'}</LoadingLabel> : (hiddenMode ? '숨김 해제' : '숨기기')}
                                        </Button>
                                    <ChevronRightIcon sx={{
                                        fontSize: 18, color: '#bbb',
                                        transform: expandedReviewId === review.id ? 'rotate(90deg)' : 'none',
                                        transition: 'transform 0.15s ease',
                                    }} />
                                </Box>
                            </Box>
                            {expandedReviewId === review.id && (
                                <Box sx={{ px: 1.6, pb: 1.6, borderTop: '1px solid #f1f5f9' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, py: 1.4 }}>
                                        <Box sx={{ px: 0.4, py: 1, bgcolor: '#fff' }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#64748b', mb: 0.55 }}>
                                                {review.parentReviewId ? '추가 의견' : '사용자 의견'}
                                            </Typography>
                                            <Typography sx={{ fontSize: 16, color: '#1a1a1a', fontWeight: 500, lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                                                {review.content}
                                            </Typography>
                                        </Box>
                                        {replies.map((reply, index) => {
                                            const draftKey = reviewReplyDraftKey(review, reply);
                                            const editing = editingReplyKey === draftKey;
                                            const canEditReply = reply.legacy || (Number.isFinite(Number(reply.id)) && Number(reply.id) > 0);
                                            return (
                                                <Box key={reply.id || index} sx={{
                                                    px: 1.55,
                                                    py: 1.35,
                                                    bgcolor: editing ? '#fff' : '#fffdf2',
                                                    color: '#1a1a1a',
                                                    border: editing ? '1px solid #111827' : '1px solid #f3ecd7',
                                                    borderRadius: 3,
                                                }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0, flexWrap: 'wrap' }}>
                                                                <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#8a7000' }}>
                                                                    관리자 답변 {replies.length > 1 ? index + 1 : ''}
                                                                </Typography>
                                                                {!editing && (
                                                                    <Box sx={readStatusChipSx(reply.userReadAt)}>
                                                                        {readStatusLabel(reply.userReadAt)}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {!editing && canEditReply && (
                                                                <Button
                                                                    size="small"
                                                                    onMouseDown={(event) => event.preventDefault()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        handleStartEditReply(review, reply);
                                                                    }}
                                                                    sx={{ minWidth: 0, px: 0.7, py: 0.1, color: '#8a7000', fontSize: 12, fontWeight: 900, borderRadius: 2 }}
                                                                >
                                                                    수정하기
                                                                </Button>
                                                            )}
                                                        </Box>
                                                        {editing ? (
                                                            <>
                                                                <TextField
                                                                    autoFocus
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={3}
                                                                    value={replyDrafts[draftKey] ?? reply.content ?? ''}
                                                                    onChange={(e) => setReplyDrafts(prev => ({ ...prev, [draftKey]: e.target.value }))}
                                                                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
                                                                />
                                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        disabled={savingId === draftKey}
                                                                        onMouseDown={(event) => event.preventDefault()}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            handleCancelEditReply(draftKey);
                                                                        }}
                                                                        sx={{ borderRadius: 2.5, fontWeight: 900, color: '#64748b', borderColor: '#e5e7eb' }}
                                                                    >
                                                                        취소
                                                                    </Button>
                                                                    <Button
                                                                        variant="contained"
                                                                        size="small"
                                                                        disabled={savingId === draftKey}
                                                                        onMouseDown={(event) => event.preventDefault()}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            handleReply(review, reply.id);
                                                                        }}
                                                                        sx={{ bgcolor: '#111827', color: '#fff', borderRadius: 2.5, fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#1f2937' } }}
                                                                    >
                                                                        {savingId === draftKey ? <LoadingLabel>저장 중</LoadingLabel> : '수정하기'}
                                                                    </Button>
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.62, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                                                                    {reply.content}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: '#a16207', fontWeight: 700, mt: 0.7, textAlign: 'right' }}>
                                                                    {formatDateTime(reply.createdAt)}
                                                                    {reply.userReadAt ? ` · 읽은 시간 ${formatDateTime(reply.userReadAt)}` : ''}
                                                                </Typography>
                                                            </>
                                                        )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    {review.parentReviewId && (
                                        <Box component="details" sx={{ mb: 2, '& summary': { cursor: 'pointer', color: '#64748b', fontSize: 13, py: 1.5 } }}>
                                            <Box component="summary">이전 대화 보기</Box>
                                            <Box sx={{ py: 1.25, mb: 1 }}>
                                                <SingleLineText sx={{ fontSize: 12, fontWeight: 900, color: '#64748b', mb: 0.45 }}>
                                                    이전 의견
                                                </SingleLineText>
                                                <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 700, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                                                    {review.parentReviewContent || '원본 건의를 찾을 수 없어요'}
                                                </Typography>
                                            </Box>
                                            {((review.parentReplies || []).length > 0 ? review.parentReplies : review.parentAdminReply ? [{ id: 'legacy-parent', content: review.parentAdminReply }] : []).map((reply, index) => (
                                                <Box key={reply.id || index} sx={{ p: 1.25, bgcolor: '#fffdf2', borderRadius: 2.5, mt: index === 0 ? 0 : 1 }}>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 900, color: '#8a7000', mb: 0.45 }}>
                                                        {((review.parentReplies || []).length > 1) ? `이전 답변 ${index + 1}` : '이전 답변'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 14, color: '#1a1a1a', fontWeight: 750, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}>
                                                        {reply.content}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #eef2f7' }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            maxRows={5}
                                            placeholder={hasReplies ? '추가 답변 입력' : '답변 입력'}
                                            value={replyDrafts[newReplyDraftKey] ?? ''}
                                            onChange={(e) => setReplyDrafts(prev => ({ ...prev, [newReplyDraftKey]: e.target.value }))}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#fff' } }}
                                        />
                                        <Button
                                            variant="contained"
                                            aria-label="답변 보내기"
                                            disabled={savingId === newReplyDraftKey}
                                            onClick={() => handleReply(review)}
                                            sx={{ minWidth: 52, height: 46, bgcolor: '#111827', color: '#fff', borderRadius: 2.5, fontWeight: 900, boxShadow: 'none', '&:hover': { bgcolor: '#1f2937' } }}
                                        >
                                            {savingId === newReplyDraftKey ? <LoadingLabel /> : <SendIcon sx={{ fontSize: 20 }} />}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                    })}
                                </Box>
                                </Box>}
                            </Box>
                        );
                    })}
                    <Box sx={{ minHeight: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.7, py: hasMore ? 1.4 : 0.4 }}>
                        {hasMore && (
                            <>
                                <Typography sx={{ fontSize: 12, color: '#999', fontWeight: 700 }}>
                                    {visibleGroups.length} / {groupedRows.length}
                                </Typography>
                                <SingleLineText sx={{ fontSize: 13, color: '#64748b', fontWeight: 800 }}>
                                    아래로 스크롤하면 더 불러와요
                                </SingleLineText>
                            </>
                        )}
                    </Box>
                </>
            )}
        </>
    );
}

function ReviewCtaTab({ reviewCtaStats, reviewPeriod, setReviewPeriod }) {
    const stats = reviewCtaStats || EMPTY_REVIEW_CTA_STATS;
    const periodOption = AD_PERIOD_OPTIONS.find(option => option.value === reviewPeriod) || AD_PERIOD_OPTIONS[0];
    const periodLabel = stats.trendScopeLabel || periodOption.helper;
    const summaryLabel = stats.summaryScopeLabel || periodOption.summaryHelper;
    return (
        <>
            <SectionTitle title="리뷰 CTA" desc="스토어 리뷰 요청이 실제로 어떻게 반응되는지 봅니다" />
            <AdPeriodSelector value={reviewPeriod} onChange={setReviewPeriod} />
            <DefinitionCard
                title="해석 기준"
                body="리뷰 남기기는 스토어 화면으로 이동한 버튼 클릭 수입니다. 스토어 정책상 실제 리뷰 작성 완료 여부는 알 수 없어서, 클릭한 사용자는 다시 CTA를 띄우지 않는 완료 사용자로 봅니다."
            />
            <KpiGrid>
                <SummaryCard label="CTA 노출" value={formatNumber(stats.shown)} color="#111827" helper={`${summaryLabel} 리뷰 CTA가 열린 횟수`} />
                <SummaryCard label="리뷰 남기기" value={formatNumber(stats.reviewClicked)} color="#16a34a" helper={`${summaryLabel} 스토어 리뷰 버튼 클릭`} />
                <SummaryCard label="클릭률" value={formatPercent(stats.clickRate)} color="#2563eb" helper="리뷰 남기기 / CTA 노출" />
                <SummaryCard label="나중에" value={formatNumber(stats.dismissed)} color="#f59e0b" helper={`${summaryLabel} 나중에 선택 또는 닫기`} />
                <SummaryCard label="나중에율" value={formatPercent(stats.dismissRate)} color="#ef4444" helper="나중에 / CTA 노출" />
                <SummaryCard label="CTA 사용자" value={formatNumber(stats.uniqueGroups || stats.uniqueDevices)} color="#7c3aed" helper={`${summaryLabel} CTA 이벤트가 있는 유니크 사용자`} />
            </KpiGrid>
            <LineChart
                title="응답 추이"
                subtitle={`${periodLabel} · 노출 후 선택 행동을 같이 봅니다`}
                data={sortByDateDesc(stats.eventTrend || [])}
                series={[
                    { key: 'shown', label: '노출', color: '#2563eb' },
                    { key: 'reviewClicked', label: '리뷰 남기기', color: '#16a34a' },
                    { key: 'dismissed', label: '나중에', color: '#f59e0b' },
                ]}
                labelZeroValues={false}
            />
            <LineChart
                title="노출 위치 추이"
                subtitle={`${periodLabel} · CTA가 어디서 열렸는지 봅니다`}
                data={sortByDateDesc(stats.sourceTrend || [])}
                series={[
                    { key: 'medication_action', label: REVIEW_CTA_SOURCE_LABELS.medication_action, color: '#2563eb' },
                    { key: 'monthly_recap', label: REVIEW_CTA_SOURCE_LABELS.monthly_recap, color: '#7c3aed' },
                ]}
                labelZeroValues={false}
            />
            <ReviewSourceBreakdownCard rows={stats.sourceBreakdown || []} />
            <CountListCard title="CTA 이벤트" rows={stats.byEventType || []} labelKey="eventType" labelMap={REVIEW_CTA_EVENT_LABELS} color="#2563eb" suffix="건" />
            <CountListCard title="노출 위치" rows={stats.bySource || []} labelKey="source" labelMap={REVIEW_CTA_SOURCE_LABELS} color="#16a34a" suffix="건" />
        </>
    );
}

const ALIAS_STATUS_OPTIONS = [
    { value: 'PENDING', label: '대기' },
    { value: 'APPROVED', label: '승인' },
    { value: 'REJECTED', label: '거절' },
    { value: 'EXCLUDED', label: '자동 제외' },
];
const ALIAS_STATUS_LABELS = {
    PENDING: '대기',
    APPROVED: '승인',
    REJECTED: '거절',
    EXCLUDED: '자동 제외',
};
const CATEGORY_LABELS = {
    PRESCRIPTION: '처방약',
    SUPPLEMENT: '영양제',
    HERBAL: '한약',
    OTC: '일반약',
    OTHER: '기타',
};

function MedicineCandidatesTab({ setSnackbar }) {
    const PAGE_SIZE = 20;
    const [acting, setActing] = useState(null);
    const [status, setStatus] = useState('PENDING');
    const [rows, setRows] = useState([]);
    const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMedicineAliasCandidates(status, visibleLimit);
            const items = Array.isArray(data) ? data : (data.items || []);
            setRows(items);
            setTotalCount(Array.isArray(data) ? items.length : (data.totalCount || 0));
        } catch {
            setSnackbar({ open: true, message: '약 후보를 불러오지 못했어요', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [setSnackbar, status, visibleLimit]);

    useEffect(() => { load(); }, [load]);

    const handleStatusChange = (nextStatus) => {
        setStatus(nextStatus);
        setVisibleLimit(PAGE_SIZE);
    };

    const approve = async (row) => {
        if (acting) return;
        setActing({ id: row.id, action: 'approve' });
        try {
            await approveMedicineAliasCandidate(row.id, { displayName: row.displayName, category: row.category });
            setSnackbar({ open: true, message: '검색 후보로 승인했어요', severity: 'success' });
            load();
        } catch (err) {
            const message = err?.response?.data?.message || '승인에 실패했어요';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally { setActing(null); }
    };

    const reject = async (row) => {
        if (acting) return;
        setActing({ id: row.id, action: 'reject' });
        try {
            await rejectMedicineAliasCandidate(row.id);
            setSnackbar({ open: true, message: '후보를 거절했어요', severity: 'success' });
            load();
        } catch (err) {
            const message = err?.response?.data?.message || '거절에 실패했어요';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally { setActing(null); }
    };

    return (
        <>
            <SectionTitle title="약 검색 후보" desc="직접 입력된 약 이름을 검토한 뒤 검색 후보로 노출해요" />
            <DefinitionCard
                title="승인 기준"
                body="직접 입력값은 바로 노출하지 않습니다. 관계·시간·모양·테스트 문자열은 자동 제외되고, 관리자가 승인한 이름만 검색 결과의 등록 이름으로 표시됩니다."
            />

            <Box sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', mb: 1.5 }}>
                {ALIAS_STATUS_OPTIONS.map(option => (
                    <Button
                        key={option.value}
                        onClick={() => handleStatusChange(option.value)}
                        sx={{
                            flexShrink: 0, borderRadius: 99, px: 1.6, py: 0.7,
                            bgcolor: status === option.value ? '#ffe566' : '#fff',
                            color: status === option.value ? '#5a4a00' : '#777',
                            border: '1px solid #eee', fontWeight: 800,
                        }}
                    >
                        {option.label}
                    </Button>
                ))}
            </Box>

            <CardBox>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: rows.length > 0 ? 1.2 : 0 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#888', fontWeight: 800 }}>
                        {formatNumber(rows.length)}/{formatNumber(totalCount)}개 표시
                    </Typography>
                </Box>
                {loading ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: '#8a7000' }} /></Box>
                ) : rows.length === 0 ? (
                    <SingleLineText sx={{ py: 3, textAlign: 'center', color: '#999', fontWeight: 700 }}>표시할 후보가 없어요</SingleLineText>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {rows.map(row => (
                            <Box key={row.id} sx={{ p: 1.2, border: '1px solid #f0f0f0', borderRadius: 2, bgcolor: '#fafafa' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                    <Typography sx={{ fontWeight: 900, color: '#222', fontSize: '1rem' }}>{row.displayName}</Typography>
                                    <Typography sx={{ fontSize: '0.78rem', color: '#8a7000', fontWeight: 900, flexShrink: 0 }}>{formatNumber(row.useCount)}회</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.82rem', color: '#777', mb: 0.4 }}>
                                    원본: {row.rawName} · {CATEGORY_LABELS[row.category] || row.category} · {ALIAS_STATUS_LABELS[row.status] || row.status}
                                </Typography>
                                {row.rejectReason && (
                                    <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', mb: 0.6 }}>제외/거절: {row.rejectReason}</Typography>
                                )}
                                <Typography sx={{ fontSize: '0.76rem', color: '#aaa', mb: 1 }}>
                                    최근 {formatDateTime(row.lastSeenAt)}
                                </Typography>
                                {row.status === 'PENDING' && (
                                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                                        <Button disabled={!!acting} onClick={() => approve(row)} sx={{ flex: 1, borderRadius: 2, bgcolor: '#22c55e', color: '#fff', fontWeight: 900, '&:hover': { bgcolor: '#16a34a' } }}>
                                            {acting?.id === row.id && acting.action === 'approve' ? <LoadingLabel /> : '승인'}
                                        </Button>
                                        <Button disabled={!!acting} onClick={() => reject(row)} sx={{ flex: 1, borderRadius: 2, bgcolor: '#fff', color: '#ef4444', border: '1px solid #fecaca', fontWeight: 900 }}>
                                            {acting?.id === row.id && acting.action === 'reject' ? <LoadingLabel /> : '거절'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        ))}
                        {rows.length < totalCount && (
                            <Button
                                fullWidth
                                onClick={() => setVisibleLimit(prev => prev + PAGE_SIZE)}
                                sx={{
                                    mt: 0.5, py: 1.1, borderRadius: 2,
                                    bgcolor: '#fff', color: '#5a4a00',
                                    border: '1px solid #eee', fontWeight: 900,
                                }}
                            >
                                더보기
                            </Button>
                        )}
                    </Box>
                )}
            </CardBox>
        </>
    );
}

function ToolsTab({
    currentUuid,
    handleCopyUuid,
    handleCollect,
    collecting,
    deployInfo,
    appRuntimeInfo,
    myDeviceInfo,
    myEntitlements,
    adFreeUpdating,
    adFreeScope,
    setAdFreeScope,
    handleGrantAdFree,
    handleRevokeAdFree,
    handleResetStoreReviewLocalState,
}) {
    return (
        <>
            <SectionTitle title="확인/도구" desc="지표가 아닌 테스트와 운영 확인 정보입니다" />
            <AdminOperations />
            <DefinitionCard
                title="수동 수집"
                body="수동 수집은 어제 날짜의 일별 사실값을 다시 계산합니다. 오늘 수치는 여기서 확정되지 않고 실시간 스냅샷으로만 봅니다."
            />
            <CardBox>
                <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 1 }}>현재 기기 UUID</SingleLineText>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: '#555', fontFamily: 'monospace', overflowWrap: 'anywhere', lineHeight: 1.45 }}>
                        {currentUuid}
                    </Typography>
                    <IconButton
                        onClick={handleCopyUuid}
                        disabled={currentUuid === '-'}
                        sx={{ width: 36, height: 36, bgcolor: '#f5f5f5', color: '#666', flexShrink: 0, '&:hover': { bgcolor: '#ececec' }, '&.Mui-disabled': { color: '#ccc' } }}
                    >
                        <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </CardBox>

            {deployInfo && (
                <CardBox>
                    <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 1 }}>배포 정보</SingleLineText>
                    <InfoRow label="빌드 시각" value={formatDateTime(deployInfo.buildTime)} />
                    <InfoRow label="서버 시작" value={formatDateTime(deployInfo.serverStartTime)} />
                </CardBox>
            )}

            <CardBox>
                <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 1 }}>현재 앱 번들</SingleLineText>
                <InfoRow label="앱 버전" value={appRuntimeInfo?.appVersion || '-'} />
                <InfoRow label="플랫폼" value={appRuntimeInfo?.platform || '-'} />
                <InfoRow label="Runtime" value={appRuntimeInfo?.runtimeVersion || '-'} />
                <InfoRow label="채널" value={appRuntimeInfo?.channel || (appRuntimeInfo?.isDev ? 'dev-client' : '-')} />
                <InfoRow label="Update ID" value={appRuntimeInfo?.updateId || (appRuntimeInfo?.isEmbeddedLaunch ? 'embedded' : '-')} mono />
                <InfoRow label="Update 시각" value={formatDateTime(appRuntimeInfo?.updateCreatedAt)} />
                <InfoRow label="내장 번들" value={appRuntimeInfo ? (appRuntimeInfo.isEmbeddedLaunch ? '예' : '아니오') : '-'} />
                <InfoRow label="수신 시각" value={formatDateTime(appRuntimeInfo?.receivedAt)} />
            </CardBox>

            <CardBox>
                <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 1 }}>내 기기(DB)</SingleLineText>
                <InfoRow label="앱 버전" value={myDeviceInfo?.appVersion || '-'} />
                <InfoRow label="플랫폼" value={myDeviceInfo?.platform || '-'} />
                <InfoRow label="스토어" value={myDeviceInfo?.store || '-'} />
                <InfoRow label="Runtime" value={myDeviceInfo?.runtimeVersion || '-'} />
                <InfoRow label="채널" value={myDeviceInfo?.channel || '-'} />
                <InfoRow label="OS 버전" value={myDeviceInfo?.osVersion || '-'} />
                <InfoRow label="기기 모델" value={myDeviceInfo?.deviceModel || '-'} />
                <InfoRow label="마지막 활동" value={formatDateTime(myDeviceInfo?.lastActiveAt)} />
                <InfoRow label="삭제 처리" value={myDeviceInfo ? (myDeviceInfo.deleted ? '예' : '아니오') : '-'} />
                <InfoRow label="삭제 사유" value={myDeviceInfo?.deletedReason || '-'} />
            </CardBox>

            <CardBox>
                <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 1 }}>광고 제거</SingleLineText>
                <InfoRow label="상태" value={myEntitlements?.adFree ? '활성화됨' : '비활성화됨'} />
                <InfoRow label="지급 경로" value={myEntitlements?.source || '-'} />
                <InfoRow label="적용 범위" value={myEntitlements?.scope === 'DEVICE' ? '이 기기만' : (myEntitlements?.scope === 'GROUP' ? '로그인 그룹 전체' : '-')} />
                <InfoRow label="만료" value={myEntitlements?.expiresAt ? formatDateTime(myEntitlements.expiresAt) : (myEntitlements?.adFree ? '영구' : '-')} />
                <InfoRow label="관리자 지급" value={myEntitlements?.adminGrantedAdFree ? '있음' : '없음'} />
                <Typography sx={{ fontSize: '0.82rem', color: '#888', mt: 1 }}>
                    회수는 이 기기·현재 그룹의 관리자 지급분에만 적용돼요. 유료 구매 권한은 유지돼요.
                </Typography>
                {!myEntitlements?.adminGrantedAdFree && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
                        {[
                            { value: 'DEVICE', label: '이 기기만' },
                            { value: 'GROUP', label: '로그인 그룹 전체' },
                        ].map(option => {
                            const selected = adFreeScope === option.value;
                            return (
                                <Button
                                    key={option.value}
                                    onClick={() => setAdFreeScope(option.value)}
                                    disabled={adFreeUpdating}
                                    sx={{
                                        borderRadius: 2,
                                        py: 0.9,
                                        fontSize: '0.82rem',
                                        fontWeight: 900,
                                        bgcolor: selected ? '#fff9db' : '#fff',
                                        color: selected ? '#5a4a00' : '#555',
                                        border: selected ? '1px solid #facc15' : '1px solid #e5e7eb',
                                        '&:hover': { bgcolor: selected ? '#fff4b8' : '#f9fafb' },
                                        '&.Mui-disabled': { color: '#bbb', borderColor: '#eee' },
                                    }}
                                >
                                    {option.label}
                                </Button>
                            );
                        })}
                    </Box>
                )}
                <Button
                    fullWidth
                    onClick={myEntitlements?.adminGrantedAdFree ? handleRevokeAdFree : handleGrantAdFree}
                    disabled={adFreeUpdating || !myEntitlements || currentUuid === '-'}
                    sx={{
                        mt: 1.5,
                        borderRadius: 3,
                        py: 1.2,
                        fontWeight: 900,
                        bgcolor: myEntitlements?.adminGrantedAdFree ? '#fff5f5' : '#ecfdf5',
                        color: myEntitlements?.adminGrantedAdFree ? '#dc2626' : '#15803d',
                        border: myEntitlements?.adminGrantedAdFree ? '1px solid #fecaca' : '1px solid #bbf7d0',
                        '&:hover': { bgcolor: myEntitlements?.adminGrantedAdFree ? '#fee2e2' : '#dcfce7' },
                        '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#bbb', borderColor: '#eee' },
                    }}
                >
                    {adFreeUpdating ? <LoadingLabel /> : (myEntitlements?.adminGrantedAdFree ? '관리자 지급분 회수' : '영구 광고 제거 지급')}
                </Button>
            </CardBox>

            <CardBox>
                <SingleLineText sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>내 폰 리뷰 CTA 완전 초기화</SingleLineText>
                <Typography sx={{ fontSize: '0.82rem', color: '#888', mb: 1.2, lineHeight: 1.45 }}>
                    이 WebView의 리뷰 CTA localStorage를 모두 삭제합니다. 리뷰 남기기를 눌렀던 완료 상태도 테스트용으로 초기화합니다.
                </Typography>
                <Button
                    fullWidth
                    onClick={handleResetStoreReviewLocalState}
                    sx={{
                        borderRadius: 3,
                        py: 1.2,
                        fontWeight: 900,
                        bgcolor: '#fff7ed',
                        color: '#c2410c',
                        border: '1px solid #fed7aa',
                        '&:hover': { bgcolor: '#ffedd5' },
                        '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#bbb', borderColor: '#eee' },
                    }}
                >
                    리뷰 CTA 완전 초기화
                </Button>
            </CardBox>

            <Button
                fullWidth
                onClick={handleCollect}
                disabled={collecting}
                sx={{
                    borderRadius: 3, py: 1.4, fontSize: '1rem', fontWeight: 800,
                    bgcolor: '#ffe566', color: '#5a4a00', mb: 2,
                    '&:hover': { bgcolor: '#e8d040' },
                    '&:disabled': { bgcolor: '#f0f0f0', color: '#ccc' },
                }}
            >
                {collecting ? <LoadingLabel>수집 중...</LoadingLabel> : '어제 통계 수동 수집'}
            </Button>
        </>
    );
}

const AD_FEATURE_LABELS = {
    medication_action: '복약 처리 후 전면 광고',
    monthly_recap: '지난달 회고',
    ai_chat_reward: 'AI 상담 보상형',
    smart_suggest_reward: '스마트 분석 보상형',
    medication_slot_reward: '약 등록 슬롯 보상형',
    guardian_nudge_reward: '가족 알림 보상형',
    preload: '광고 사전 로드',
    unknown: '미분류',
};

const AD_TYPE_LABELS = {
    interstitial: '전면 광고',
    rewarded: '보상형 광고',
};

const AD_EVENT_LABELS = {
    request: '요청',
    show: '표시됨',
    wait_started: '표시 대기',
    unavailable: '표시 불가',
    error: '오류',
    load_started: '준비 시작',
    loaded: '준비 완료',
    load_error: '준비 오류',
    preload_error: '준비 실패',
    load_stale_reset: '준비 재시도',
    closed: '닫힘',
    reward_earned: '보상 지급',
};

const REVIEW_CTA_EVENT_LABELS = {
    shown: '노출',
    dismissed: '나중에',
    review_clicked: '리뷰 남기기',
};

const REVIEW_CTA_SOURCE_LABELS = {
    medication_action: '복용 완료',
    monthly_recap: '월간 회고',
    unknown: '미분류',
};

function adLabel(map, value) {
    return map[value] || value || '-';
}

function AdPeriodSelector({ value, onChange }) {
    return (
        <CardBox sx={{ py: 1.4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.7 }}>
                {AD_PERIOD_OPTIONS.map(option => (
                    <Button
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        sx={{
                            borderRadius: 2.5,
                            py: 1,
                            px: 0.8,
                            fontWeight: 900,
                            bgcolor: value === option.value ? '#ffe566' : '#f3f4f6',
                            color: value === option.value ? '#5a4a00' : '#777',
                            '&:hover': { bgcolor: value === option.value ? '#e8d040' : '#e5e7eb' },
                        }}
                    >
                        <Box>
                            <SingleLineText sx={{ fontSize: '0.9rem', fontWeight: 900, lineHeight: 1.2 }}>{option.label}</SingleLineText>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, opacity: 0.72, lineHeight: 1.2 }}>{option.helper}</Typography>
                        </Box>
                    </Button>
                ))}
            </Box>
        </CardBox>
    );
}

function CountListCard({ title, rows, labelKey, labelMap, color, suffix = '' }) {
    const max = Math.max(...(rows || []).map(r => Number(r.count || 0)), 1);
    return (
        <CardBox>
            <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1.5 }}>{title}</SingleLineText>
            {(rows || []).length === 0 ? (
                <SingleLineText sx={{ fontSize: '0.86rem', color: '#999' }}>선택 기간에 데이터가 없어요</SingleLineText>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rows.map((row) => (
                        <Box key={row[labelKey]} sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 48px', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#333', minWidth: 0 }}>
                                {adLabel(labelMap, row[labelKey])}
                            </Typography>
                            <Box sx={{ height: 10, bgcolor: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
                                <Box sx={{ height: '100%', width: `${(Number(row.count || 0) / max) * 100}%`, bgcolor: color, borderRadius: 5 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.86rem', color: '#333', fontWeight: 800, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {formatNumber(row.count)}{suffix}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </CardBox>
    );
}

function FailureReasonsCard({ rows }) {
    return (
        <CardBox>
            <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a', mb: 0.6 }}>실패 원인</SingleLineText>
            <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 1.5, lineHeight: 1.5 }}>
                표시 불가와 preload 실패를 원인 메시지별로 묶어서 봅니다.
            </Typography>
            {(rows || []).length === 0 ? (
                <SingleLineText sx={{ fontSize: '0.86rem', color: '#999' }}>선택 기간에 실패 기록이 없어요</SingleLineText>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rows.map((row, idx) => (
                        <Box key={`${row.eventType}-${row.feature}-${row.adType}-${idx}`} sx={{ p: 1.2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #f1f5f9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#ef4444' }}>
                                    {formatNumber(row.count)}건
                                </Typography>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#333' }}>
                                    {adLabel(AD_EVENT_LABELS, row.eventType)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#2563eb' }}>
                                    {adLabel(AD_FEATURE_LABELS, row.feature)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: '#16a34a' }}>
                                    {adLabel(AD_TYPE_LABELS, row.adType)}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: row.reason ? '#666' : '#aaa', overflowWrap: 'anywhere', lineHeight: 1.45 }}>
                                {row.reason || '원인 메시지 없음'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </CardBox>
    );
}

function ReviewSourceBreakdownCard({ rows }) {
    return (
        <CardBox>
            <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a', mb: 0.6 }}>위치별 반응</SingleLineText>
            <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 1.5, lineHeight: 1.5 }}>
                같은 CTA라도 복용 완료 직후와 월간 회고 완료 직후의 반응 차이를 봅니다.
            </Typography>
            {(rows || []).length === 0 ? (
                <SingleLineText sx={{ fontSize: '0.86rem', color: '#999' }}>선택 기간에 데이터가 없어요</SingleLineText>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {rows.map(row => (
                        <Box key={row.source} sx={{ p: 1.2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #f1f5f9' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: '#333' }}>
                                    {adLabel(REVIEW_CTA_SOURCE_LABELS, row.source)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#2563eb' }}>
                                    클릭률 {formatPercent(row.clickRate)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                                <MetricItem label="노출" value={formatNumber(row.shown)} color="#111827" />
                                <MetricItem label="리뷰 남기기" value={formatNumber(row.reviewClicked)} color="#16a34a" />
                                <MetricItem label="나중에" value={`${formatNumber(row.dismissed)} · ${formatPercent(row.dismissRate)}`} color="#f59e0b" />
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}
        </CardBox>
    );
}

function PeriodSelector({ days, setDays }) {
    return (
        <CardBox sx={{ py: 1.4 }}>
            <Box sx={{ display: 'flex', gap: 0.6, justifyContent: 'center' }}>
                {[7, 30, 90].map(d => (
                    <Button
                        key={d}
                        onClick={() => setDays(d)}
                        sx={{
                            borderRadius: 3, px: 2, py: 0.75, fontSize: '0.95rem', fontWeight: 800,
                            minWidth: 0,
                            bgcolor: days === d ? '#ffe566' : '#f3f4f6',
                            color: days === d ? '#5a4a00' : '#777',
                            '&:hover': { bgcolor: days === d ? '#e8d040' : '#e5e7eb' },
                        }}
                    >
                        {d}일
                    </Button>
                ))}
            </Box>
        </CardBox>
    );
}

function TodayMedicationActionCard({ actions }) {
    const totalActionLogs = Number(actions.takenLogs || 0) + Number(actions.skippedLogs || 0) + Number(actions.deferredLogs || 0);
    const actionUsers = actions.actionUsers ?? actions.totalGroups;
    return (
        <CardBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                <Box>
                    <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>오늘 복약 액션 사용자</SingleLineText>
                    <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.2 }}>
                        접속 후 실제 약 처리 행동을 한 유니크 사용자
                    </SingleLineText>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: '#aaa', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {actions.date || '오늘'}
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.4 }}>
                <MetricItem label="유니크 사용자" value={formatNumber(actionUsers)} color="#16a34a" />
                <MetricItem label="활성 기기" value={formatNumber(actions.totalDevices)} color="#2563eb" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 1.2 }}>
                <MetricItem label="복용 완료" value={formatNumber(actions.takenGroups)} color="#16a34a" />
                <MetricItem label="건너뜀" value={formatNumber(actions.skippedGroups)} color="#6b7280" />
                <MetricItem label="나중에 알림" value={formatNumber(actions.deferredGroups)} color="#f59e0b" />
            </Box>
            <Typography sx={{ fontSize: '0.78rem', color: '#999', lineHeight: 1.5 }}>
                전체는 사용자 기준 중복 제거 · 행동별 숫자는 중복 가능 · 처리 로그 {formatNumber(totalActionLogs)}건
            </Typography>
        </CardBox>
    );
}

function DistributionCard({ title, dist, avgKey, maxKey, color, error }) {
    const maxBucketCount = Math.max(...dist.buckets.map(b => b.count), 1);
    return (
        <CardBox>
            <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1.5 }}>{title}</SingleLineText>
            {error && (
                <SingleLineText sx={{ fontSize: '0.82rem', color: '#ef4444', mb: 1.5 }}>
                    분포 데이터를 불러오지 못했어요
                </SingleLineText>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mb: 2 }}>
                <MetricItem label="평균" value={Number(dist[avgKey] || 0).toFixed(1)} color={color} />
                <MetricItem label="최대" value={formatNumber(dist[maxKey] || 0)} color="#ef4444" />
                <MetricItem label="그룹" value={formatNumber(dist.totalGroups)} color="#16a34a" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {dist.buckets.map((b) => (
                    <Box key={b.label} sx={{ display: 'grid', gridTemplateColumns: '60px 1fr 40px', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: '#666', fontWeight: 600 }}>{b.label}</Typography>
                        <Box sx={{ height: 12, bgcolor: '#f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${(b.count / maxBucketCount) * 100}%`, bgcolor: color, borderRadius: 6 }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#333', fontWeight: 800, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {b.count}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </CardBox>
    );
}

function NotificationPatternCard({ patterns }) {
    const medicationMix = patterns?.medicationMix || [];
    const groupModes = patterns?.groupModes || [];
    const groupAdoption = patterns?.groupAdoption || [];
    const readiness = patterns?.pushReadiness || {};
    const settings = patterns?.reminderSettings || [];
    const ringtoneMed = findPatternMetric(medicationMix, 'FULLSCREEN');
    const vibrationMed = findPatternMetric(medicationMix, 'VIBRATION');
    const pushMed = findPatternMetric(medicationMix, 'PUSH');
    const ringtoneGroups = findPatternMetric(groupAdoption, 'FULLSCREEN');
    const pushOnly = findPatternMetric(groupModes, 'PUSH_ONLY');
    const vibrationOnly = findPatternMetric(groupModes, 'VIBRATION_ONLY');
    const ringtoneOnly = findPatternMetric(groupModes, 'FULLSCREEN_ONLY');
    const mixed = findPatternMetric(groupModes, 'MIXED');
    const ringtoneSetting = settings.find(item => item.type === 'FULLSCREEN') || {};
    const vibrationSetting = settings.find(item => item.type === 'VIBRATION') || {};
    const pushSetting = settings.find(item => item.type === 'PUSH') || {};
    const insights = buildNotificationInsights(patterns, {
        ringtoneMed,
        pushMed,
        ringtoneGroups,
        pushOnly,
        ringtoneOnly,
        mixed,
        readiness,
    });

    return (
        <CardBox>
            <Box sx={{ mb: 1.6 }}>
                <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>알림 방식 패턴</SingleLineText>
                <Typography sx={{ fontSize: '0.82rem', color: '#888', mt: 0.25, lineHeight: 1.45 }}>
                    활성 약과 약 등록 그룹 기준으로 푸시, 진동, 벨소리 알람 선택을 봅니다
                </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.6 }}>
                <MetricItem label="벨소리 약 비율" value={formatPercent(ringtoneMed.rate)} color="#ef4444" />
                <MetricItem label="진동 약 비율" value={formatPercent(vibrationMed.rate)} color="#64748b" />
                <MetricItem label="벨소리 사용 그룹" value={formatPercent(ringtoneGroups.rate)} color="#dc2626" />
                <MetricItem label="둘 다 쓰는 그룹" value={formatPercent(mixed.rate)} color="#7c3aed" />
                <MetricItem label="푸시 준비율" value={formatPercent(readiness.rate)} color="#16a34a" />
            </Box>
            <PatternBar
                title="활성 약 기준"
                subtitle={`${formatNumber(patterns?.totalActiveMedications)}개 약`}
                items={[
                    { ...pushMed, color: '#2563eb' },
                    { ...vibrationMed, color: '#64748b' },
                    { ...ringtoneMed, color: '#ef4444' },
                ]}
            />
            <PatternBar
                title="사용자 그룹 기준"
                subtitle={`${formatNumber(patterns?.totalMedicationGroups)}개 그룹`}
                items={[
                    { ...pushOnly, color: '#2563eb' },
                    { ...vibrationOnly, color: '#64748b' },
                    { ...ringtoneOnly, color: '#ef4444' },
                    { ...mixed, color: '#7c3aed' },
                ]}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 1.4, mb: 1.4 }}>
                <ReminderSettingMini setting={pushSetting} color="#2563eb" />
                <ReminderSettingMini setting={vibrationSetting} color="#64748b" />
                <ReminderSettingMini setting={ringtoneSetting} color="#ef4444" />
            </Box>
            <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 2, p: 1.2 }}>
                <SingleLineText sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#7c6a00', mb: 0.7 }}>
                    해석
                </SingleLineText>
                <Box component="ul" sx={{ m: 0, pl: 2.1 }}>
                    {insights.map((text) => (
                        <Typography component="li" key={text} sx={{ fontSize: '0.82rem', color: '#5f6368', lineHeight: 1.55, mb: 0.35 }}>
                            {text}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </CardBox>
    );
}

function PatternBar({ title, subtitle, items }) {
    const total = items.reduce((sum, item) => sum + Number(item.count || 0), 0);
    return (
        <Box sx={{ mb: 1.3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.7 }}>
                <SingleLineText sx={{ fontSize: '0.86rem', fontWeight: 800, color: '#333' }}>{title}</SingleLineText>
                <SingleLineText sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#999' }}>{subtitle}</SingleLineText>
            </Box>
            <Box sx={{ display: 'flex', height: 14, bgcolor: '#f1f5f9', borderRadius: 7, overflow: 'hidden', mb: 0.7 }}>
                {items.map((item) => {
                    const width = total > 0 ? (Number(item.count || 0) / total) * 100 : 0;
                    return <Box key={item.type} sx={{ width: `${width}%`, bgcolor: item.color, minWidth: width > 0 ? 2 : 0 }} />;
                })}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {items.map((item) => (
                    <Box key={item.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.45 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#666', fontWeight: 700 }}>
                            {item.label} {formatNumber(item.count)} · {formatPercent(item.rate)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function ReminderSettingMini({ setting, color }) {
    return (
        <Box sx={{ p: 1.2, bgcolor: '#fafafa', border: '1px solid #f1f5f9', borderRadius: 1 }}>
            <Typography sx={{ fontSize: '0.8rem', color: '#333', fontWeight: 900, mb: 0.7 }}>
                {setting?.label || '-'}
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: '#888', lineHeight: 1.55 }}>
                첫 재알림 {formatNumber(setting?.avgInitialIntervalMinutes)}분
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: '#888', lineHeight: 1.55 }}>
                최대 {formatNumber(setting?.avgMaxReminderMinutes)}분
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color, fontWeight: 800, lineHeight: 1.55 }}>
                {setting?.type === 'FULLSCREEN' ? `평균 볼륨 ${formatNumber(setting?.avgAlarmVolume)}%` : `${formatNumber(setting?.medications)}개 약`}
            </Typography>
        </Box>
    );
}

function findPatternMetric(items, type) {
    return items.find(item => item.type === type) || { label: '-', type, count: 0, rate: 0 };
}

function buildNotificationInsights(patterns, metrics) {
    const totalGroups = Number(patterns?.totalMedicationGroups || 0);
    if (totalGroups === 0) return ['아직 약 등록 그룹이 없어 알림 방식 패턴을 판단하기 어렵습니다.'];

    const ringtoneRate = Number(metrics.ringtoneMed.rate || 0);
    const ringtoneGroupRate = Number(metrics.ringtoneGroups.rate || 0);
    const mixedRate = Number(metrics.mixed.rate || 0);
    const pushReadyRate = Number(metrics.readiness.rate || 0);
    const insights = [];

    if (ringtoneRate >= 50) {
        insights.push(`등록 약의 ${formatPercent(ringtoneRate)}가 벨소리 알람입니다. 핵심 가치인 강한 알림을 실제 설정까지 연결하고 있습니다.`);
    } else if (ringtoneRate >= 20) {
        insights.push(`등록 약의 ${formatPercent(ringtoneRate)}가 벨소리 알람입니다. 일부 사용자는 중요한 약에만 강한 알림을 선택하는 패턴입니다.`);
    } else {
        insights.push(`벨소리 알람 약 비율이 ${formatPercent(ringtoneRate)}입니다. 온보딩과 약 등록 화면에서 벨소리 알람의 차별점을 더 분명히 보여줄 여지가 있습니다.`);
    }

    if (mixedRate >= 15) {
        insights.push(`둘 다 쓰는 그룹이 ${formatPercent(mixedRate)}입니다. 약 중요도에 따라 푸시와 벨소리를 나눠 쓰는 고관여 사용자가 보입니다.`);
    } else if (ringtoneGroupRate > ringtoneRate + 10) {
        insights.push('벨소리 사용 그룹 비율이 약 기준 비율보다 높습니다. 여러 약 중 일부에만 벨소리를 켜는 선택 사용이 많습니다.');
    } else {
        insights.push(`푸시만 쓰는 그룹이 ${formatPercent(metrics.pushOnly.rate)}입니다. 기본값인 푸시가 아직 주 사용 방식입니다.`);
    }

    if (pushReadyRate < 85) {
        insights.push(`푸시 준비율이 ${formatPercent(pushReadyRate)}입니다. 약을 등록했지만 토큰이 없는 그룹이 있어 권한 안내나 토큰 갱신 상태 점검이 필요합니다.`);
    } else {
        insights.push(`푸시 준비율은 ${formatPercent(pushReadyRate)}로 높습니다. 알림 방식 선택보다 실제 도달 문제는 작아 보입니다.`);
    }

    return insights;
}

function FeatureUsageCard({ usage }) {
    const adoptions = usage?.adoptions || [];
    const features = usage?.features || [];
    const symptoms = usage?.symptoms || {};
    const activityItems = [
        ...features.map((f) => ({
            key: f.feature,
            label: f.label,
            total: f.totalUsers,
            recent: f.recentUsers30Days,
            calls: f.recentCalls30Days,
            suffix: '회',
        })),
        {
            key: 'symptoms',
            label: '증상 기록',
            total: symptoms.totalUsers,
            recent: symptoms.recentUsers30Days,
            calls: symptoms.recentLogs30Days,
            suffix: '건',
        },
    ];
    return (
        <CardBox>
            <Box sx={{ mb: 1.8 }}>
                <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>선택 기능 사용</SingleLineText>
                <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.25, lineHeight: 1.45 }}>
                    사용자가 직접 켜거나 등록한 기능만 봅니다
                </SingleLineText>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.2 }}>
                {adoptions.map((f) => (
                    <AdoptionUsageItem
                        key={f.label}
                        label={f.label}
                        users={f.users}
                        rate={f.rate}
                        basis={f.basis}
                        unit={f.unit}
                    />
                ))}
            </Box>
            <Box sx={{ pt: 1.8, borderTop: '1px solid #f1f5f9', mb: 1.4 }}>
                <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>기타 사용량</SingleLineText>
                <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.25, lineHeight: 1.45 }}>
                    최근 30일에 실제로 얼마나 쓰였는지 봅니다
                </SingleLineText>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {activityItems.map((item) => (
                    <FeatureUsageItem key={item.key} item={item} />
                ))}
            </Box>
        </CardBox>
    );
}

function AdoptionUsageItem({ label, users, rate, basis, unit = '그룹' }) {
    const pct = Math.max(0, Math.min(Number(rate || 0), 100));
    return (
        <Box sx={{ py: 1.1, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 0 } }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(160px, 1fr) 92px 110px' }, gap: 1, alignItems: 'center', mb: 0.8 }}>
                <Box sx={{ minWidth: 0 }}>
                    <SingleLineText sx={{ fontSize: '0.94rem', fontWeight: 800, color: '#333', lineHeight: 1.25 }}>
                        {label}
                    </SingleLineText>
                    <Typography sx={{ fontSize: '0.72rem', color: '#999', mt: 0.35, lineHeight: 1.3 }}>
                        기준: {basis}
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: '1rem', color: '#2563eb', fontWeight: 900, whiteSpace: 'nowrap', lineHeight: 1.15, textAlign: { xs: 'left', sm: 'right' } }}>
                    {formatPercent(rate)}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#666', fontWeight: 800, whiteSpace: 'nowrap', textAlign: { xs: 'left', sm: 'right' } }}>
                    {formatNumber(users)}{unit} 사용 중
                </Typography>
            </Box>
            <Box sx={{ height: 8, bgcolor: '#eef2f7', borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: '#2563eb', borderRadius: 4 }} />
            </Box>
        </Box>
    );
}

function FeatureUsageItem({ item }) {
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 1fr) 110px 110px 110px' },
            gap: { xs: 0.6, sm: 1 },
            alignItems: 'center',
            py: 1,
            borderBottom: '1px solid #f1f5f9',
            '&:last-child': { borderBottom: 0 },
        }}>
            <SingleLineText sx={{ fontSize: '0.94rem', fontWeight: 800, color: '#333' }}>{item.label}</SingleLineText>
            <UsageCell label="30일 사용자" value={formatNumber(item.recent)} color="#2563eb" />
            <UsageCell label="30일 사용량" value={`${formatNumber(item.calls)}${item.suffix}`} color="#111827" />
            <UsageCell label="누적 사용자" value={formatNumber(item.total)} color="#777" />
        </Box>
    );
}

function UsageCell({ label, value, color }) {
    return (
        <Box sx={{ display: { xs: 'flex', sm: 'block' }, justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
            <SingleLineText sx={{ fontSize: '0.72rem', color: '#999', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</SingleLineText>
            <Typography sx={{ fontSize: '0.9rem', color, fontWeight: 900, mt: { xs: 0, sm: 0.2 }, textAlign: { xs: 'right', sm: 'left' }, whiteSpace: 'nowrap' }}>
                {value}
            </Typography>
        </Box>
    );
}

function LineChart({ title, subtitle, data, series, percent = false, showPointLabels = true, labelZeroValues = true, valueFormatter, preserveMissing = false, missingValueLabel }) {
    data = sortByDateDesc(data);
    if (data.length === 0) {
        return (
            <CardBox>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                    <Box>
                        <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>{title}</SingleLineText>
                        {subtitle && <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.2 }}>{subtitle}</SingleLineText>}
                    </Box>
                </Box>
                <Box sx={{
                    height: 150,
                    borderRadius: 3,
                    bgcolor: '#f8fafc',
                    border: '1px dashed #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <SingleLineText sx={{ fontSize: '0.9rem', color: '#999', fontWeight: 700 }}>
                        선택한 기간에 수집된 데이터가 없어요
                    </SingleLineText>
                </Box>
            </CardBox>
        );
    }

    const width = Math.max(data.length * 42, 320);
    const height = 180;
    const left = 28;
    const right = 14;
    const top = 30;
    const bottom = 34;
    const innerW = width - left - right;
    const innerH = height - top - bottom;
    const values = data.flatMap(d => series.map(s => Number(d[s.key] || 0)));
    const max = percent ? 100 : Math.max(...values, 1);
    const xFor = (i) => left + (data.length <= 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
    const yFor = (v) => top + innerH - (Number(v || 0) / max) * innerH;
    const hasValue = value => !preserveMissing || (value != null && Number.isFinite(Number(value)));
    const formatChartValue = (value, row, seriesItem) => {
        const n = Number(value || 0);
        if (!Number.isFinite(n)) return percent ? '0%' : '0';
        if (valueFormatter) return valueFormatter(n, row, seriesItem);
        return percent ? `${n.toFixed(0)}%` : n.toLocaleString();
    };
    const labelYFor = (value, seriesIndex) => {
        const y = yFor(value);
        const baseOffset = series.length === 1 ? -9 : (seriesIndex % 2 === 0 ? -9 : 15);
        const labelY = y + baseOffset;
        if (labelY < 10) return y + 15;
        if (labelY > height - bottom - 4) return y - 9;
        return labelY;
    };
    // Several OS series can share the same value. Keep their numeric labels apart.
    const labelPositions = data.map(row => {
        const positions = series.map((s, index) => {
            const value = Number(row[s.key] || 0);
            if (!showPointLabels || s.showPointLabels === false ||
                (hasValue(row[s.key]) ? !labelZeroValues && value === 0 : !missingValueLabel)) return null;
            return labelYFor(value, index);
        });
        if (series.length < 3) return positions;
        const ordered = positions.map((y, index) => ({ y, index })).filter(label => label.y != null)
            .sort((a, b) => a.y - b.y || a.index - b.index);
        ordered.forEach((label, i) => {
            label.y = Math.max(label.y, i === 0 ? 10 : ordered[i - 1].y + 14);
        });
        for (let i = ordered.length - 1; i >= 0; i -= 1) {
            ordered[i].y = Math.min(ordered[i].y, i === ordered.length - 1 ? height - bottom - 4 : ordered[i + 1].y - 14);
            positions[ordered[i].index] = ordered[i].y;
        }
        return positions;
    });

    return (
        <CardBox>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'flex-start' },
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
            }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <SingleLineText sx={{ fontWeight: 800, color: '#1a1a1a' }}>{title}</SingleLineText>
                    {subtitle && <SingleLineText sx={{ fontSize: '0.82rem', color: '#888', mt: 0.2 }}>{subtitle}</SingleLineText>}
                </Box>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    maxWidth: '100%',
                }}>
                    {series.map(s => (
                        <Box key={s.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, minWidth: 'max-content' }}>
                            <Box sx={{ width: 8, height: 8, bgcolor: s.color, borderRadius: '50%', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.72rem', color: '#777', fontWeight: 700, whiteSpace: 'nowrap' }}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box sx={{ overflowX: 'auto', mx: -1, px: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
                <svg role="img" aria-label={title} width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', minWidth: '100%' }}>
                    {[0, 0.5, 1].map((r) => (
                        <line key={r} x1={left} x2={width - right} y1={top + innerH * r} y2={top + innerH * r} stroke="#eef2f7" strokeWidth="1" />
                    ))}
                    {series.map((s) => {
                        const segments = [[]];
                        data.forEach((d, i) => {
                            if (hasValue(d[s.key])) segments[segments.length - 1].push(`${xFor(i)},${yFor(d[s.key])}`);
                            else if (segments[segments.length - 1].length) segments.push([]);
                        });
                        return (
                            <g key={s.key} data-series={s.key}>
                                {segments.filter(points => points.length).map((points, i) => <polyline key={i} points={points.join(' ')} fill="none" stroke={s.color} strokeWidth={s.strokeWidth || 2.5} strokeLinecap="round" strokeLinejoin="round" />)}
                                {data.map((d, i) => {
                                    if (!hasValue(d[s.key])) return showPointLabels && s.showPointLabels !== false && missingValueLabel ? (
                                        <text key={`${s.key}-${d.date}`} x={xFor(i)} y={labelPositions[i][series.indexOf(s)]} textAnchor="middle" fontSize="10" fontWeight="700" fill="#999">{missingValueLabel}</text>
                                    ) : null;
                                    const value = Number(d[s.key] || 0);
                                    return (
                                        <g key={`${s.key}-${d.date}`}>
                                            <circle cx={xFor(i)} cy={yFor(value)} r="3" fill={s.color} />
                                            {showPointLabels && s.showPointLabels !== false && (labelZeroValues || value !== 0) && (
                                                <text
                                                    x={xFor(i)}
                                                    y={labelPositions[i][series.indexOf(s)]}
                                                    textAnchor="middle"
                                                    fontSize="10"
                                                    fontWeight="700"
                                                    fill={s.color}
                                                    stroke="#fff"
                                                    strokeWidth="3"
                                                    paintOrder="stroke"
                                                >
                                                    {formatChartValue(value, d, s)}
                                                </text>
                                            )}
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}
                    {data.map((d, i) => (
                        <text key={d.date} x={xFor(i)} y={height - 10} textAnchor="middle" fontSize="10" fill="#999">
                            {formatDate(d.date)}
                        </text>
                    ))}
                </svg>
            </Box>
        </CardBox>
    );
}

function SectionTitle({ title, desc }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <SingleLineText sx={{ fontSize: '1.18rem', fontWeight: 900, color: '#1a1a1a' }}>{title}</SingleLineText>
            <Typography sx={{ fontSize: '0.9rem', color: '#888', mt: 0.3 }}>{desc}</Typography>
        </Box>
    );
}

function DefinitionCard({ title, body }) {
    return (
        <CardBox sx={{ py: 1.5 }}>
            <SingleLineText sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#7c6a00', letterSpacing: '0.04em', textTransform: 'uppercase', mb: 0.6 }}>
                {title}
            </SingleLineText>
            <Typography sx={{ fontSize: '0.84rem', color: '#5f6368', lineHeight: 1.6 }}>
                {body}
            </Typography>
        </CardBox>
    );
}

function KpiGrid({ children }) {
    return <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2, mb: 2 }}>{children}</Box>;
}

function CardBox({ children, sx = {} }) {
    return (
        <Card sx={{ borderRadius: 3, mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ py: 2, ...sx, '&:last-child': { pb: sx.pb ?? 2 } }}>
                {children}
            </CardContent>
        </Card>
    );
}

function MetricItem({ label, value, color }) {
    return (
        <Box sx={{ textAlign: 'center' }}>
            <SingleLineText sx={{ fontSize: '0.75rem', color: '#999', mb: 0.3 }}>{label}</SingleLineText>
            <Typography sx={{ fontSize: '1.08rem', fontWeight: 800, color }}>{value}</Typography>
        </Box>
    );
}

function SummaryCard({ label, value, color, helper }) {
    return (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ py: 1.8, '&:last-child': { pb: 1.8 } }}>
                <SingleLineText sx={{ fontSize: '0.8rem', color: '#777', mb: 0.5, fontWeight: 700 }}>{label}</SingleLineText>
                <Typography sx={{ fontSize: '1.45rem', fontWeight: 900, color, lineHeight: 1.15 }}>{value}</Typography>
                {helper && <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.5, lineHeight: 1.35 }}>{helper}</Typography>}
            </CardContent>
        </Card>
    );
}

function InfoRow({ label, value, mono = false }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.5 }}>
            <SingleLineText sx={{ fontSize: '0.85rem', color: '#777', flexShrink: 0 }}>{label}</SingleLineText>
            <Typography sx={{
                fontSize: '0.85rem',
                color: '#333',
                fontWeight: 600,
                textAlign: 'right',
                minWidth: 0,
                overflowWrap: 'anywhere',
                fontFamily: mono ? 'monospace' : undefined,
            }}>{value}</Typography>
        </Box>
    );
}
